import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, isRecord, textOf, unwrapList, unwrapRecord } from './api-client';
import type { AdminApiClient, JsonRecord } from './api-client';
import { pathFor } from './page-registry';
import { CmsComponentOrderForm } from './typed-forms';
import { CmsComponentForm, CmsComponentUpdateForm } from './cms-component-editor';
import './admin-round03-layout.css';
import './order-management.css';
import './order-list-first.css';
import { OrderBatchActions, orderSelectionBlocked } from './order-batch-actions';
import { CopyOrderButton } from './order-copy';
import { MiniappPageLinks } from './miniapp-page-links';
import { AUTHORIZED_SCOPE } from './authorized-scope-client';

type Params = Record<string, string>;
type LoadState<T = unknown> = { data?: T; error?: unknown; loading: boolean; reload: () => void };

interface CanonicalPageProps {
  api: AdminApiClient;
  targetAppId: string;
  pageId: string;
  params: Params;
  scopeFilter?: React.ReactNode;
  scopePagination?: React.ReactNode;
  onFiltersChange?: () => void;
}

const statusLabels: Record<string, string> = {
  ACTIVE: '已启用',
  DISABLED: '已停用',
  DRAFT: '草稿',
  PUBLISHED: '已发布',
  ROLLED_BACK: '已回滚',
  PENDING_PAYMENT: '待支付',
  PAID_NEED_TRIP_INFO: '待补资料',
  PAID_INFO_READY: '待运营确认',
  PENDING_CONFIRM: '待运营确认',
  CONFIRMED: '已确认',
  PENDING_FULFILLMENT: '待履约',
  FULFILLMENT_READY: '履约准备完成',
  IN_SERVICE: '服务进行中',
  PAYMENT_CLOSED: '支付已关闭',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  REFUND_APPLIED: '退款申请中',
  REFUND_PROCESSING: '退款处理中',
  REFUNDED: '已退款',
  AFTER_SALES: '售后处理中',
  AFTER_SALES_CLOSED: '售后已关闭',
  APPLIED: '待审核',
  OPEN: '待处理',
  APPROVED: '已同意',
  REJECTED: '已驳回',
  PROCESSING: '处理中',
  RESOLVED: '已解决',
  CLOSED: '已关闭',
  SUCCEEDED: '已成功',
  NOT_INITIATED: '未发起',
  FAILED: '失败',
  READY: '可下载',
  PENDING: '等待处理',
  APP: '小程序',
  CITY: '城市',
  PRODUCT: '产品',
  PHONE: '电话',
  WECHAT: '企业微信',
  IN_APP: '站内消息',
  OTHER: '其他',
  INBOUND: '用户来电',
  OUTBOUND: '主动联系',
  INTERNAL: '内部备注',
  QUICK: '快捷预订',
  STANDARD: '标准预订',
  OPEN_PRODUCT: '打开产品详情',
  OPEN_CUSTOMER_SERVICE: '打开在线客服',
  OPEN_ENTERPRISE_WECHAT_GUIDE: '打开企业微信向导',
  SWITCH_CITY: '切换城市',
  OPEN_AGREEMENT: '打开服务协议',
  ADDED: '新增',
  REMOVED: '移除',
  CHANGED: '修改',
  LOW: '常规',
  MEDIUM: '需复核',
  HIGH: '高风险',
  CHARTER_GUIDE_PACKAGE: '包车 + 向导 + 套餐',
  TRANSFER_TICKET_PACKAGE: '接送 + 票务 + 套餐',
  CARPOOL_GUIDE_PACKAGE: '拼车 + 向导 + 套餐',
  PER_PERSON: '按人计价',
  PER_CAR: '按车计价',
  PER_PACKAGE: '按套餐计价',
  INCLUDED: '已包含',
  SELF_PAY: '游客自理',
  NOT_INCLUDED: '不包含',
};

const checklistLabels: Record<string, string> = {
  TRIP_INFO_VERIFIED: '客户资料已确认',
  CUSTOMER_CONTACTED: '已联系客户',
  VEHICLE_CONFIRMED: '车辆 / 司机已回填',
  GUIDE_CONFIRMED: '向导 / 服务人员已回填',
  TICKET_CONFIRMED: '票务处理已说明',
  PICKUP_TIME_CONFIRMED: '最终出发时间已确认',
};

function useTask<T>(key: string, loader: () => Promise<T>, dependency: unknown): LoadState<T> {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<Omit<LoadState<T>, 'reload'>>({ loading: true });
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    void loader().then(
      (data) => active && setState({ loading: false, data }),
      (error: unknown) => active && setState({ loading: false, error }),
    );
    return () => {
      active = false;
    };
  }, [key, revision, dependency]);
  return { ...state, reload: () => setRevision((value) => value + 1) };
}

function useApi(api: AdminApiClient, path?: string) {
  return useTask(
    path ?? 'round03-disabled',
    () => (path ? api.get(path) : Promise.resolve({})),
    api,
  );
}

function combineStates(...states: LoadState[]): LoadState {
  return {
    loading: states.some((state) => state.loading),
    error: states.find((state) => state.error)?.error,
    reload: () => states.forEach((state) => state.reload()),
  };
}

function rows(value: unknown, key?: string): JsonRecord[] {
  if (key && isRecord(value) && Array.isArray(value[key])) return value[key].filter(isRecord);
  return unwrapList(value);
}

function nested(record: JsonRecord, key: string): JsonRecord {
  return isRecord(record[key]) ? record[key] : {};
}

function nestedRows(record: JsonRecord, key: string): JsonRecord[] {
  return Array.isArray(record[key]) ? record[key].filter(isRecord) : [];
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function clean(value: unknown, fallback = '暂未提供') {
  if (value === null || value === undefined || value === '' || value === '—') return fallback;
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value === 'object') return fallback;
  return String(value);
}

function label(value: unknown, fallback = '待确认') {
  const raw = clean(value, fallback);
  return statusLabels[raw] ?? statusLabels[raw.toUpperCase()] ?? fallback;
}

function money(value: unknown) {
  const cents = Number(value);
  return Number.isFinite(cents)
    ? `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '暂未提供';
}

function compactMoney(value: unknown) {
  const cents = Number(value);
  return Number.isFinite(cents) ? `¥${Math.round(cents / 100)}起` : '价格待配置';
}

function protectedMoney(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? money(value) : '无权限查看';
}

function dateTime(value: unknown) {
  if (typeof value !== 'string') return '暂未记录';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '暂未记录';
  return parsed.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function dateOnly(value: unknown) {
  if (typeof value !== 'string' || !value) return '待补充';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match?.[1] ?? '待补充';
}

function paymentLabel(value: unknown) {
  const raw = clean(value, 'NOT_INITIATED');
  return (
    {
      SUCCEEDED: '已支付',
      PENDING: '支付处理中',
      PENDING_PAYMENT: '待支付',
      FAILED: '支付失败',
      CLOSED: '支付已关闭',
      NOT_INITIATED: '未发起',
    }[raw] ?? '待确认'
  );
}

function tone(value: unknown) {
  const raw = String(value);
  if (['ACTIVE', 'PUBLISHED', 'COMPLETED', 'SUCCEEDED', 'READY', 'RESOLVED'].includes(raw))
    return 'green';
  if (['FAILED', 'REFUND_APPLIED', 'REFUND_PROCESSING', 'AFTER_SALES'].includes(raw)) return 'red';
  if (['DISABLED', 'CANCELLED', 'REFUNDED', 'CLOSED', 'AFTER_SALES_CLOSED'].includes(raw))
    return 'gray';
  return 'orange';
}

function Pill({ value }: { value: unknown }) {
  return <span className={`canonical-pill ${tone(value)}`}>{label(value)}</span>;
}

function Header({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="canonical-page-title">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {actions && <div className="canonical-page-actions">{actions}</div>}
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="r3-scope" role="status">
      <span>✓</span>
      <strong>{children}</strong>
    </div>
  );
}

function Panel({
  title,
  hint,
  actions,
  children,
  className = '',
}: {
  title: string;
  hint?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`r3-panel ${className}`}>
      <header>
        <div>
          <h2>{title}</h2>
          {hint && <p>{hint}</p>}
        </div>
        {actions && <div className="r3-panel-actions">{actions}</div>}
      </header>
      <div className="r3-panel-body">{children}</div>
    </section>
  );
}

function Metrics({
  items,
  className = '',
}: {
  items: Array<{ label: string; value: React.ReactNode; note?: string }>;
  className?: string;
}) {
  return (
    <div className={`r3-metrics ${className}`}>
      {items.map((item) => (
        <article key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          <>{item.note && <small>{item.note}</small>}</>
        </article>
      ))}
    </div>
  );
}

function Empty({ noun, detail }: { noun: string; detail?: string }) {
  return (
    <div className="canonical-state empty" role="status">
      <span className="canonical-state__icon">◇</span>
      <strong>暂无{noun}</strong>
      {detail && <p>{detail}</p>}
    </div>
  );
}

function State({
  state,
  count,
  noun,
  children,
}: {
  state: LoadState;
  count: number;
  noun: string;
  children: React.ReactNode;
}) {
  if (state.loading)
    return (
      <div className="canonical-state loading" role="status">
        <span className="canonical-spinner" />
        <strong>正在加载{noun}</strong>
      </div>
    );
  if (state.error) {
    const permission = state.error instanceof ApiError && state.error.isPermissionError;
    return (
      <div className={`canonical-state ${permission ? 'permission' : 'error'}`} role="alert">
        <span className="canonical-state__icon">{permission ? '▣' : '!'}</span>
        <strong>{permission ? '当前账号无权查看' : `${noun}加载失败`}</strong>
        <p>
          {permission
            ? '请由管理员检查功能权限和数据范围。'
            : '请检查网络后重试；系统不会展示内部错误详情。'}
        </p>
        <button type="button" onClick={state.reload}>
          重试
        </button>
      </div>
    );
  }
  return count ? <>{children}</> : <Empty noun={noun} />;
}

function Table({
  heads,
  empty,
  children,
}: {
  heads: string[];
  empty: boolean;
  children: React.ReactNode;
}) {
  if (empty) return <Empty noun="符合条件的记录" />;
  return (
    <div className="canonical-table-wrap">
      <table className="canonical-business-table">
        <thead>
          <tr>
            {heads.map((head) => (
              <th key={head}>{head}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Filters({
  query,
  onQuery,
  statusText = '全部状态',
  placeholder = '输入关键词筛选',
  statusValue,
  onStatus,
  statusOptions = [],
}: {
  query: string;
  onQuery: (value: string) => void;
  statusText?: string;
  placeholder?: string;
  statusValue?: string;
  onStatus?: ((value: string) => void) | undefined;
  statusOptions?: string[];
}) {
  return (
    <div className="canonical-filter-card r3-filters">
      <span className="r3-locked-filter" aria-label="小程序范围">
        当前小程序
      </span>
      <span className="r3-locked-filter" aria-label="城市范围">
        全部授权城市
      </span>
      {onStatus ? (
        <select
          aria-label="订单状态"
          value={statusValue}
          onChange={(event) => onStatus(event.target.value)}
        >
          <option value="">全部订单状态</option>
          {statusOptions.map((value) => (
            <option key={value} value={value}>
              {label(value)}
            </option>
          ))}
        </select>
      ) : (
        <span className="r3-locked-filter" aria-label="业务状态">
          {statusText}
        </span>
      )}
      <label className="canonical-search">
        <span>搜索</span>
        <input
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={placeholder}
        />
      </label>
    </div>
  );
}

function Field({ name, value }: { name: string; value: React.ReactNode }) {
  return (
    <div className="r3-field">
      <span>{name}</span>
      <strong>{value}</strong>
    </div>
  );
}

function mutationKey(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function MutationButton({
  api,
  path,
  label: buttonLabel,
  body,
  method = 'POST',
  idempotent = false,
  danger = false,
  onDone,
  disabled = false,
}: {
  api: AdminApiClient;
  path: string;
  label: string;
  body?: unknown;
  method?: 'POST' | 'PUT' | 'PATCH';
  idempotent?: boolean;
  danger?: boolean;
  onDone: () => void;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function run() {
    setBusy(true);
    setMessage('');
    try {
      await api.request(path, {
        method,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        ...(idempotent ? { headers: { 'idempotency-key': mutationKey('admin-r3') } } : {}),
      });
      setMessage('操作已提交');
      onDone();
    } catch (error) {
      setMessage(operationError(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="r3-command">
      <button
        type="button"
        className={danger ? 'r3-danger' : 'canonical-primary'}
        disabled={busy || disabled}
        onClick={() => void run()}
      >
        {busy ? '提交中…' : buttonLabel}
      </button>
      {message && <small role="status">{message}</small>}
    </span>
  );
}

function CmsReleaseCommand({
  api,
  path,
  expectedVersion,
  action,
  onDone,
}: {
  api: AdminApiClient;
  path: string;
  expectedVersion: number;
  action: '发布' | '回滚';
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [key, setKey] = useState(() =>
    mutationKey(`cms-${action === '发布' ? 'publish' : 'rollback'}`),
  );
  async function submit() {
    setBusy(true);
    setMessage('');
    try {
      await api.request(path, {
        method: 'POST',
        headers: { 'idempotency-key': key },
        body: JSON.stringify({ expectedVersion, reason: reason.trim() }),
      });
      setMessage(`${action}已完成，版本记录和审计日志已更新`);
      setReason('');
      setConfirmed(false);
      setKey(mutationKey(`cms-${action === '发布' ? 'publish' : 'rollback'}`));
      onDone();
    } catch (error) {
      setMessage(operationError(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="r3-command-stack">
      <label className="r3-note-field">
        {action}原因
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={`请填写本次${action}的业务依据`}
          maxLength={1000}
        />
      </label>
      <label className="r3-inline-check">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        已核对页面范围、版本差异和受控动作
      </label>
      <button
        type="button"
        className={action === '回滚' ? 'r3-danger' : 'canonical-primary'}
        disabled={busy || !confirmed || !reason.trim()}
        onClick={() => void submit()}
      >
        {busy ? '提交中…' : `确认${action}`}
      </button>
      {message && <small role="status">{message}</small>}
    </div>
  );
}

function pageTitle(page: JsonRecord) {
  const code = clean(page.pageCode, '未命名页面');
  const names: Record<string, string> = {
    'FE-CITY-001': '昆明城市首页',
    'FE-CITY-002': '大理城市首页',
    'FE-CITY-003': '丽江城市首页',
    'FE-CITY-004': '香格里拉城市首页',
    'FE-CITY-005': '城市选择页',
    'FE-DETAIL-001': '产品详情首页',
    'FE-DETAIL-002': '行程介绍',
    'FE-DETAIL-003': '费用与规则',
    'FE-DETAIL-004': '购买须知',
  };
  return names[code] ?? code;
}

function operationError(error: unknown) {
  if (!(error instanceof ApiError)) return '操作失败，请稍后重试';
  const messages: Record<string, string> = {
    PERMISSION_DENIED: '当前账号没有执行此操作的权限',
    DATA_SCOPE_DENIED: '当前数据不在你的管理范围内',
    FIELD_PERMISSION_DENIED: '当前账号无权访问所需字段',
    BUSINESS_STATE_CONFLICT: '业务状态已变化，请刷新后重试',
    VALIDATION_FAILED: '提交内容不完整或格式不正确',
    IDEMPOTENCY_CONFLICT: '相同操作正在处理，请勿重复提交',
    CMS_COPY_PRIVATE_CONTENT_FORBIDDEN: '源页面包含小程序私有配置，不能跨小程序复制',
    CMS_ACTION_NOT_ALLOWED: '页面包含不受支持的组件动作，请先修正后再复制',
    CMS_COMPONENT_INVALID: '页面组件或模板已失效，请先修正后再复制',
    NOT_FOUND: '目标记录不存在或已不可用',
  };
  return messages[error.code] ?? '操作未成功，请刷新后重试';
}

function componentLabel(value: unknown, fallback = '内容组件') {
  const raw = clean(value, fallback);
  const names: Record<string, string> = {
    'product-hero': '产品主图',
    'Product Hero': '产品主图',
    'hero-banner': '页面主视觉',
    'Hero Banner': '页面主视觉',
    'text-block': '图文内容',
    'Text Block': '图文内容',
    notice: '服务提示',
    Notice: '服务提示',
    'feature-grid': '服务亮点',
    'Feature Grid': '服务亮点',
    'agreement-link': '预订须知与退款规则',
    'Agreement Link': '预订须知与退款规则',
  };
  return names[raw] ?? (/^[a-z0-9_-]+$/u.test(raw) ? fallback : raw);
}

function componentActionSummary(value: unknown) {
  const actions = stringList(value);
  if (!actions.length) return '无跳转动作';
  return actions.map((action) => label(action)).join('、');
}

function pageTypeLabel(page: JsonRecord) {
  const code = textOf(page, 'pageCode');
  if (code.includes('CITY')) return '城市首页';
  if (code.includes('DETAIL')) return '线路详情';
  if (code.includes('ORDER')) return '提交订单';
  if (code.includes('SKU')) return '套餐选择';
  return label(page.scopeType, '小程序页面');
}

function productLeadPrice(product: JsonRecord) {
  const direct = product.minSalePriceCents ?? product.salePriceCents ?? product.priceCents;
  if (typeof direct === 'number') return direct;
  const skus = nestedRows(product, 'skus');
  const prices = skus
    .map((sku) => Number(sku.salePriceCents))
    .filter((price) => Number.isFinite(price));
  return prices.length ? Math.min(...prices) : undefined;
}

function primarySku(product: JsonRecord) {
  const skus = nestedRows(product, 'skus');
  return skus.find((sku) => textOf(sku, 'status') === 'ACTIVE') ?? skus[0] ?? {};
}

function skuPeopleText(sku: JsonRecord) {
  const min = Number(sku.minPeople);
  const max = Number(sku.maxPeople);
  if (Number.isFinite(min) && Number.isFinite(max)) return `${min}-${max}人`;
  if (Number.isFinite(min)) return `${min}人起`;
  if (Number.isFinite(max)) return `最多${max}人`;
  return '人数按套餐规则';
}

function skuBrief(sku: JsonRecord) {
  const parts = [skuPeopleText(sku), clean(sku.vehicleType, ''), label(sku.pricingMode, '')].filter(
    Boolean,
  );
  return parts.length ? parts.join('｜') : '套餐规则由 SKU 动态提供';
}

function productTagNames(product: JsonRecord) {
  const rawTags = product.tags;
  if (Array.isArray(rawTags)) {
    return rawTags
      .map((tag) => {
        if (typeof tag === 'string') return tag;
        if (!isRecord(tag)) return '';
        const nestedTag = nested(tag, 'tag');
        return clean(tag.name ?? nestedTag.name, '');
      })
      .filter(Boolean);
  }
  return [];
}

function productRouteStops(product: JsonRecord) {
  return nestedRows(product, 'routeStops')
    .map((stop) => ({
      title: clean(stop.title ?? stop.name ?? nested(stop, 'poi').name, ''),
      description: clean(stop.description ?? stop.summary, ''),
      durationMinutes: Number(stop.durationMinutes),
    }))
    .filter((stop) => stop.title);
}

function listText(value: unknown, fallback: string) {
  const values = stringList(value);
  return values.length ? values.join('、') : fallback;
}

function diffPathLabel(value: unknown) {
  const raw = clean(value, '页面配置');
  const labels: Record<string, string> = {
    components: '页面组件',
    'components.count': '页面组件',
    'components.length': '组件数量',
    'snapshot.components': '页面组件',
    'product-list': '线路列表',
    'product-list.section.background': '线路列表背景',
    'product-list.card.gap': '线路卡片间距',
    'city:lijiang': '丽江城市覆盖',
    'city:lijiang.hero.image': '丽江主视觉',
    'city:lijiang.headline': '丽江标题文案',
    'city:lijiang.category[1]': '丽江分类标签',
    actionIds: '受控动作',
    dataBindings: '数据绑定',
  };
  if (labels[raw]) return labels[raw];
  if (raw.includes('action')) return '受控动作';
  if (raw.includes('component')) return '页面组件';
  if (raw.includes('binding')) return '数据绑定';
  if (raw.includes('hero')) return '主视觉';
  if (raw.includes('category')) return '分类标签';
  return /^[a-z0-9_.:[\]-]+$/iu.test(raw) ? '页面配置项' : raw;
}

function displayDiffValue(value: unknown): string {
  if (value === null || value === undefined) return '无';
  if (typeof value === 'boolean') return value ? '开启' : '关闭';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return `${value.length} 项配置`;
  return '结构内容已调整';
}

function displayOperationNote(value: unknown, fallback = '稳定线上版本') {
  const raw = clean(value, fallback);
  const lower = raw.toLowerCase();
  if (lower.includes('fixture city page initial publish')) return '开发测试初始发布';
  if (lower.includes('fixture second publish')) return '开发测试差异版本发布';
  if (lower.includes('fixture initial publish')) return '开发测试初始发布';
  return raw;
}

const cityServiceDefaults = [
  { iconKey: 'ticket', label: '优惠票务' },
  { iconKey: 'car', label: '正规车辆' },
  { iconKey: 'guide', label: '自由出行' },
  { iconKey: 'headset', label: '优质服务' },
  { iconKey: 'price', label: '透明报价' },
] as const;

function cmsComponentProps(components: JsonRecord[], key: string, instanceKey?: string) {
  const component = components.find(
    (entry) =>
      textOf(entry, 'componentKey') === key &&
      (!instanceKey || textOf(entry, 'instanceKey') === instanceKey),
  );
  return nested(component ?? {}, 'props');
}

function cityHeroImages(components: JsonRecord[]) {
  const images = cmsComponentProps(components, 'hero-banner').images;
  return Array.isArray(images)
    ? images
        .map((entry) => (isRecord(entry) ? textOf(entry, 'url') : clean(entry, '')))
        .filter((url) => /^https:\/\//u.test(url))
    : [];
}

function cityServices(components: JsonRecord[]) {
  const columns = cmsComponentProps(components, 'feature-grid').columns;
  if (!Array.isArray(columns)) return cityServiceDefaults;
  const configured = columns.filter(isRecord).map((entry, index) => ({
    iconKey: clean(entry.iconKey, cityServiceDefaults[index]?.iconKey ?? 'guide'),
    label: clean(entry.label, cityServiceDefaults[index]?.label ?? `服务 ${index + 1}`),
  }));
  return configured.length === 5 ? configured : cityServiceDefaults;
}

function CityServiceIcon({ kind }: { kind: string }) {
  if (kind === 'car')
    return (
      <svg viewBox="0 0 64 64">
        <path d="m13 32 5-13c1-3 3-5 7-5h14c4 0 6 2 7 5l5 13M9 35c0-4 3-7 7-7h32c4 0 7 3 7 7v11H9zM15 46v5m34-5v5M17 37h5m20 0h5" />
      </svg>
    );
  if (kind === 'guide')
    return (
      <svg viewBox="0 0 64 64">
        <circle cx="32" cy="18" r="9" />
        <path d="M16 52v-8c0-9 7-16 16-16s16 7 16 16v8M26 35l6 6 6-6M32 41v11" />
      </svg>
    );
  if (kind === 'headset')
    return (
      <svg viewBox="0 0 64 64">
        <path d="M13 35v-5c0-11 8-20 19-20s19 9 19 20v17c0 5-4 9-9 9h-7" />
        <rect x="8" y="29" width="10" height="17" rx="5" />
        <rect x="46" y="29" width="10" height="17" rx="5" />
      </svg>
    );
  if (kind === 'price')
    return (
      <svg viewBox="0 0 64 64">
        <rect x="14" y="7" width="36" height="50" rx="5" />
        <path d="m24 19 8 9 8-9M32 28v16M24 34h16" />
      </svg>
    );
  return (
    <svg viewBox="0 0 64 64">
      <path d="M9 18a5 5 0 0 0 5-5h36a5 5 0 0 0 5 5v8a7 7 0 0 0 0 12v8a5 5 0 0 0-5 5H14a5 5 0 0 0-5-5v-8a7 7 0 0 0 0-12zM32 23c-5 0-8 4-8 9s3 9 8 9 8-4 8-9-3-9-8-9z" />
    </svg>
  );
}

function CmsPhone({
  components,
  title = '页面预览',
  product,
  products = [],
  variant = 'catalog',
}: {
  components: JsonRecord[];
  title?: string;
  product?: JsonRecord;
  products?: JsonRecord[];
  variant?: 'catalog' | 'detail';
}) {
  const previewProducts =
    product && Object.keys(product).length > 0 ? [product] : products.slice(0, 3);
  const primaryProduct = previewProducts[0] ?? {};
  const sku = primarySku(primaryProduct);
  const routeStops = productRouteStops(primaryProduct);
  const tags = productTagNames(primaryProduct);
  const isDetail = variant === 'detail' && Object.keys(primaryProduct).length > 0;
  const cityHero = cmsComponentProps(components, 'hero-banner');
  const heroImages = cityHeroImages(components);
  const services = cityServices(components);
  const cost = cmsComponentProps(components, 'notice', 'cost-note');
  const costLines = clean(cost.body, '')
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean);
  return (
    <div className="r3-phone">
      <div className="r3-phone-bar">
        <span>‹</span>
        <strong>{isDetail ? '线路详情' : title}</strong>
        <span>•••</span>
      </div>
      <div className="r3-phone-body">
        {!isDetail && (
          <div className="r3-phone-city-tabs">
            <span>昆明</span>
            <span>大理</span>
            <b>{clean(cityHero.title, title).replace(/自由行$/u, '')}</b>
            <span>版纳</span>
          </div>
        )}
        <div className="r3-phone-hero" data-testid="cms-phone-canvas">
          {heroImages[0] || typeof primaryProduct.coverMediaUrl === 'string' ? (
            <>
              <img src={heroImages[0] ?? String(primaryProduct.coverMediaUrl)} alt="城市主视觉" />
              {!isDetail && (
                <div className="r3-phone-hero-copy">
                  <strong>{clean(cityHero.title, title)}</strong>
                  <span>{clean(cityHero.body, '当日往返 · 本地安心出游')}</span>
                </div>
              )}
              {heroImages.length > 1 && (
                <div className="r3-phone-carousel-dots">
                  {heroImages.slice(0, 6).map((url, index) => (
                    <i className={index === 0 ? 'active' : ''} key={url} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div>
              <strong>{isDetail ? clean(primaryProduct.title, title) : title}</strong>
              <span>
                {clean(
                  primaryProduct.subtitle ?? primaryProduct.summary,
                  '接送 · 票务式自由游 · 当日往返',
                )}
              </span>
            </div>
          )}
        </div>
        {isDetail ? (
          <>
            <article className="r3-phone-main-card">
              <div>
                <strong>{clean(primaryProduct.title, '线路产品')}</strong>
                <b>{compactMoney(productLeadPrice(primaryProduct))}</b>
              </div>
              <span>{skuBrief(sku)}</span>
              <small>
                {label(sku.serviceMode, '综合服务套餐')} · {label(sku.ticketMode, '票务按套餐说明')}
              </small>
            </article>
            <div className="r3-phone-service-grid">
              <span>🚗 接送服务</span>
              <span>🎫 {label(sku.ticketMode, '票务说明')}</span>
              <span>☷ 行程微调</span>
            </div>
            <article>
              <strong>推荐行程</strong>
              {routeStops.slice(0, 5).map((stop, index) => (
                <small key={`${stop.title}-${index}`}>
                  {`${index + 1}. ${stop.title}${Number.isFinite(stop.durationMinutes) ? ` · 约${stop.durationMinutes}分钟` : ''}`}
                </small>
              ))}
              {!routeStops.length && <span>行程节点由线路配置提供</span>}
            </article>
            <article>
              <strong>费用包含 / 不含</strong>
              <span>包含：{listText(sku.includedItems, '按所选套餐标注的服务项目')}</span>
              <span>不含：{listText(sku.excludedItems, '个人消费及未列明项目')}</span>
            </article>
            <article>
              <strong>预订须知</strong>
              <span>
                {tags.length
                  ? `标签：${tags.slice(0, 3).join('、')}`
                  : '退款与服务规则由后台规则页发布'}
              </span>
            </article>
          </>
        ) : previewProducts.length > 0 ? (
          <>
            <div className="r3-phone-assurances">
              {services.map((service) => (
                <span key={service.iconKey}>
                  <CityServiceIcon kind={service.iconKey} />
                  <small>{service.label}</small>
                </span>
              ))}
            </div>
            <div className="r3-phone-chips" aria-label="线路分类">
              <span>全部</span>
              <span>自然风光</span>
              <span>城市轻游</span>
            </div>
            <div className="r3-phone-products" data-testid="cms-preview-context">
              {previewProducts.map((item, index) => (
                <article key={textOf(item, 'id') || `preview-product-${index}`}>
                  {typeof item.coverMediaUrl === 'string' ? (
                    <img src={item.coverMediaUrl} alt="" />
                  ) : (
                    <i aria-hidden="true" />
                  )}
                  <div>
                    <strong>{clean(item.title, `线路产品 ${index + 1}`)}</strong>
                    <span>
                      {clean(item.subtitle ?? item.summary, '预览产品内容 · 当前业务数据')}
                    </span>
                    <small>
                      销量 {clean(item.salesCount ?? item.orderCount ?? (index + 1) * 920, '—')}
                      <b>{compactMoney(productLeadPrice(item))}</b>
                    </small>
                  </div>
                </article>
              ))}
            </div>
            <article className="r3-phone-cost-note">
              <strong>{clean(cost.title, '费用说明')}</strong>
              {(costLines.length
                ? costLines
                : [
                    '费用包含：司机服务费、车辆使用费、油费、过路费',
                    '无隐形强制消费，费用透明，安心出行',
                  ]
              ).map((line) => (
                <span key={line}>• {line}</span>
              ))}
            </article>
          </>
        ) : null}
        {components.length ? (
          isDetail ? null : (
            components.map((component, index) => (
              <article key={`${clean(component.instanceKey, 'component')}-${index}`}>
                <strong>
                  {componentLabel(
                    component.displayName ?? component.componentKey,
                    `内容组件 ${index + 1}`,
                  )}
                </strong>
                <span>
                  {index === 0 ? '绑定数据将在小程序运行时加载' : '组件内容已进入当前版本'}
                </span>
              </article>
            ))
          )
        ) : (
          <div className="r3-phone-empty">当前版本尚未配置组件</div>
        )}
      </div>
      <div className={`r3-phone-cta ${isDetail ? '' : 'r3-phone-cta--tabs'}`}>
        {isDetail ? (
          '立即预订'
        ) : (
          <>
            <span>客服</span>
            <span>订单</span>
            <span>我的</span>
          </>
        )}
      </div>
    </div>
  );
}

function CmsEditor({
  api,
  appId,
  pageId,
  params,
  pages,
  pagesState,
  definitions,
  definitionsState,
  onPromotionTargetChange,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  params: Params;
  pages: JsonRecord[];
  pagesState: LoadState;
  definitions: JsonRecord[];
  definitionsState: LoadState;
  onPromotionTargetChange: (id: string) => void;
}) {
  const selectedPage = params.pageId
    ? (pages.find((item) => textOf(item, 'id') === params.pageId) ?? {})
    : (pages[0] ?? {});
  const selectedId = textOf(selectedPage, 'id');
  useEffect(() => {
    onPromotionTargetChange(selectedId === '—' ? '' : selectedId);
  }, [selectedId, onPromotionTargetChange]);
  const draftState = useApi(
    api,
    selectedId !== '—'
      ? `/api/admin/m2/apps/${appId}/cms/pages/${encodeURIComponent(selectedId)}/draft`
      : undefined,
  );
  const draft = unwrapRecord(draftState.data);
  const components: JsonRecord[] = useMemo(
    () =>
      Array.isArray(draft.components)
        ? draft.components.filter(isRecord).map((component) => {
            const definition = nested(component, 'componentDefinition');
            return {
              ...component,
              componentKey: component.componentKey ?? definition.componentKey,
              displayName: component.displayName ?? definition.displayName,
              allowedActionIds: component.allowedActionIds ?? definition.allowedActionIds,
            };
          })
        : [],
    [draft.components],
  );
  const draftVersion = Number(draft.draftVersion ?? selectedPage.draftVersion ?? 0);
  const [activeComponentId, setActiveComponentId] = useState(
    () =>
      params.componentId ??
      new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get(
        'componentId',
      ) ??
      '',
  );
  const [componentSearch, setComponentSearch] = useState('');
  const activeComponent =
    components.find((item) => textOf(item, 'id') === activeComponentId) ?? components[0];
  const reloadDraft = () => {
    draftState.reload();
    pagesState.reload();
  };
  const headings: Record<string, [string, string]> = {
    'ADM-D02': ['可视化页面编辑器', '选择页面组件，维护显示内容'],
    'ADM-D03': ['组件属性配置', '选择组件后编辑内容、展示规则与受控动作'],
    'ADM-D04': ['页面图层', '维护页面组件的显示顺序'],
    'ADM-D05': ['交互动作流程', '查看当前组件的动作绑定并进入配置'],
  };
  const [title, subtitle] = headings[pageId] ?? headings['ADM-D02']!;
  const editorLinks = [
    ['添加组件', 'ADM-D02'],
    ['组件属性', 'ADM-D03'],
    ['页面图层', 'ADM-D04'],
    ['交互动作', 'ADM-D05'],
  ] as const;
  const visibleComponents = components.filter(
    (component) =>
      !componentSearch.trim() ||
      componentLabel(component.displayName ?? component.componentKey, '')
        .toLowerCase()
        .includes(componentSearch.trim().toLowerCase()),
  );
  return (
    <div className="r3-page r3-workbench">
      <Header
        title={title}
        subtitle={subtitle}
        actions={
          <>
            <a className="r3-button-link secondary" href={pathFor('ADM-D01')}>
              返回页面列表
            </a>
            {selectedId !== '—' && (
              <a
                className="r3-button-link"
                href={`${pathFor('ADM-D08')}?pageId=${encodeURIComponent(selectedId)}`}
              >
                查看页面预览
              </a>
            )}
          </>
        }
      />
      <State state={pagesState} count={pages.length} noun="页面实例">
        {selectedId === '—' ? (
          <Empty
            noun="页面配置"
            detail="该页面不存在或不属于当前所选小程序，请返回列表重新选择。"
          />
        ) : (
          <State state={combineStates(draftState, definitionsState)} count={1} noun="页面草稿">
            <div className="r3-editor-toolbar">
              <div>
                <strong>{pageTitle(selectedPage)}</strong>
                <span>草稿 V{draftVersion} · 保存草稿，发布后生效</span>
              </div>
              <nav className="r3-tabs" aria-label="CMS 编辑步骤">
                {editorLinks.map(([name, id]) => (
                  <a
                    key={id}
                    className={id === pageId ? 'active' : ''}
                    href={pathFor(id, { pageId: selectedId })}
                  >
                    {name}
                  </a>
                ))}
              </nav>
            </div>
            {pageId === 'ADM-D05' ? (
              <Panel title="页面动作绑定" hint="仅显示当前草稿中的组件。变更动作后需发布页面生效。">
                <Table
                  heads={['页面组件', '已绑定动作', '允许配置', '操作']}
                  empty={!components.length}
                >
                  {components.map((component) => (
                    <tr key={textOf(component, 'id')}>
                      <td>
                        <strong>
                          {componentLabel(component.displayName ?? component.componentKey)}
                        </strong>
                      </td>
                      <td>{componentActionSummary(component.actionIds)}</td>
                      <td>{componentActionSummary(component.allowedActionIds)}</td>
                      <td>
                        <a
                          href={`${pathFor('ADM-D03', { pageId: selectedId })}?componentId=${encodeURIComponent(textOf(component, 'id'))}`}
                        >
                          配置动作
                        </a>
                      </td>
                    </tr>
                  ))}
                </Table>
                <p className="canonical-rule-note">
                  动作只能使用系统白名单；订单、支付和退款仍由服务端校验，页面配置不能修改交易状态。
                </p>
              </Panel>
            ) : (
              <div className="r3-maintenance-layout">
                <Panel
                  title={pageId === 'ADM-D04' ? '页面结构 / 图层' : '当前页面组件'}
                  className="r3-component-selector"
                >
                  <label className="r3-cms-palette-search">
                    <span>查找已添加组件</span>
                    <input
                      type="search"
                      value={componentSearch}
                      onChange={(event) => setComponentSearch(event.target.value)}
                      placeholder="输入组件名称"
                    />
                  </label>
                  <div className="r3-component-options">
                    {visibleComponents.map((component, index) => (
                      <button
                        type="button"
                        key={textOf(component, 'id')}
                        className={
                          textOf(component, 'id') === textOf(activeComponent ?? {}, 'id')
                            ? 'active'
                            : ''
                        }
                        aria-pressed={
                          textOf(component, 'id') === textOf(activeComponent ?? {}, 'id')
                        }
                        onClick={() => setActiveComponentId(textOf(component, 'id'))}
                      >
                        <span>{Number(component.sortOrder ?? index + 1)}</span>
                        <strong>
                          {componentLabel(component.displayName ?? component.componentKey)}
                        </strong>
                        <small>{componentActionSummary(component.actionIds)}</small>
                      </button>
                    ))}
                    {!visibleComponents.length && (
                      <Empty
                        noun="匹配的页面组件"
                        detail={components.length ? '请更换搜索关键词。' : '请先添加一个组件。'}
                      />
                    )}
                  </div>
                  {pageId === 'ADM-D02' && activeComponent && (
                    <a
                      className="r3-button-link secondary"
                      href={`${pathFor('ADM-D03', { pageId: selectedId })}?componentId=${encodeURIComponent(textOf(activeComponent, 'id'))}`}
                    >
                      编辑选中组件
                    </a>
                  )}
                </Panel>
                {pageId === 'ADM-D02' ? (
                  <Panel title="添加页面组件" hint="选择组件类型后填写相关内容。">
                    <CmsComponentForm
                      api={api}
                      appId={appId}
                      pageId={selectedId}
                      definitions={definitions}
                      expectedDraftVersion={draftVersion}
                      onDone={reloadDraft}
                    />
                  </Panel>
                ) : pageId === 'ADM-D03' ? (
                  <Panel
                    title={
                      activeComponent
                        ? `组件属性 · ${componentLabel(activeComponent.displayName ?? activeComponent.componentKey)}`
                        : '组件属性'
                    }
                  >
                    {activeComponent ? (
                      <CmsComponentUpdateForm
                        key={textOf(activeComponent, 'id')}
                        api={api}
                        appId={appId}
                        pageId={selectedId}
                        component={activeComponent}
                        definitions={definitions}
                        expectedDraftVersion={draftVersion}
                        onDone={reloadDraft}
                      />
                    ) : (
                      <Empty noun="可编辑组件" detail="请先从添加组件入口建立页面内容。" />
                    )}
                  </Panel>
                ) : (
                  <Panel title="图层排序" hint="上移或下移后即时保存。">
                    <CmsComponentOrderForm
                      api={api}
                      appId={appId}
                      pageId={selectedId}
                      components={components}
                      expectedDraftVersion={draftVersion}
                      onDone={reloadDraft}
                    />
                  </Panel>
                )}
              </div>
            )}
          </State>
        )}
      </State>
    </div>
  );
}

export function CanonicalCmsPage(props: CanonicalPageProps) {
  const selectionKey = `${props.targetAppId}:${props.pageId}:${props.params.id ?? ''}:${props.params.pageId ?? ''}`;
  const [selection, setSelection] = useState<{ key: string; id: string }>();
  const onPromotionTargetChange = useCallback(
    (id: string) =>
      setSelection((current) =>
        current?.key === selectionKey && current.id === id ? current : { key: selectionKey, id },
      ),
    [selectionKey],
  );
  const targetId = selection?.key === selectionKey ? selection.id : '';
  return (
    <>
      <CmsPageContent {...props} onPromotionTargetChange={onPromotionTargetChange} />
      {targetId && (
        <MiniappPageLinks
          key={`${props.targetAppId}:${targetId}`}
          api={props.api}
          appId={props.targetAppId}
          targetType="CMS_PAGE"
          targetId={targetId}
          title="装修页面推广地址"
        />
      )}
    </>
  );
}

function CmsPageContent({
  api,
  targetAppId,
  pageId,
  params,
  onPromotionTargetChange,
}: CanonicalPageProps & { onPromotionTargetChange: (id: string) => void }) {
  const base = targetAppId ? `/api/admin/m2/apps/${targetAppId}/cms` : '';
  const templatesState = useApi(api, base ? `${base}/templates` : undefined);
  const pagesState = useApi(api, base ? `${base}/pages` : undefined);
  const definitionsState = useApi(api, base ? `${base}/definitions` : undefined);
  const appsState = useApi(api, pageId === 'ADM-D01' ? '/api/admin/m2/apps' : undefined);
  const templates = rows(templatesState.data),
    pages = rows(pagesState.data),
    definitions = rows(definitionsState.data);
  const cmsApps = rows(appsState.data).filter((app) => textOf(app, 'status') === 'ACTIVE');
  const [query, setQuery] = useState('');
  const [showTemplateCreate, setShowTemplateCreate] = useState(false);
  const [templateCode, setTemplateCode] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateMessage, setTemplateMessage] = useState('');
  const [copyTemplateId, setCopyTemplateId] = useState('');
  const [copyTemplateCode, setCopyTemplateCode] = useState('');
  const [copyTemplateName, setCopyTemplateName] = useState('');
  const [copyTemplateReason, setCopyTemplateReason] = useState('');
  const [copyTemplateBusy, setCopyTemplateBusy] = useState(false);
  const [copyTemplateMessage, setCopyTemplateMessage] = useState('');
  const [copyPageId, setCopyPageId] = useState('');
  const [copyTargetAppId, setCopyTargetAppId] = useState(targetAppId);
  const [copyPageCode, setCopyPageCode] = useState('');
  const [copyPageReason, setCopyPageReason] = useState('');
  const [copyPageBusy, setCopyPageBusy] = useState(false);
  const [copyPageMessage, setCopyPageMessage] = useState('');
  const visiblePages = useMemo(
    () =>
      pages.filter((page) => !query || pageTitle(page).toLowerCase().includes(query.toLowerCase())),
    [pages, query],
  );
  const [previewPageId, setPreviewPageId] = useState(
    () =>
      new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get(
        'pageId',
      ) ?? '',
  );
  const requestedPageId = params.id || params.pageId || previewPageId;
  const requiredScope = pageId === 'ADM-D06' ? 'CITY' : pageId === 'ADM-D07' ? 'PRODUCT' : '';
  const requestedPage = requestedPageId
    ? pages.find(
        (page) =>
          textOf(page, 'id') === requestedPageId &&
          (!requiredScope || textOf(page, 'scopeType') === requiredScope),
      )
    : undefined;
  const isEditorPage = ['ADM-D02', 'ADM-D03', 'ADM-D04', 'ADM-D05'].includes(pageId);
  const productPage =
    pages.find(
      (page) => textOf(page, 'scopeType') === 'PRODUCT' && textOf(page, 'productSpuId') !== '—',
    ) ?? pages.find((page) => textOf(page, 'pageCode').includes('DETAIL'));
  const cityPage =
    pages.find((page) => textOf(page, 'scopeType') === 'CITY') ??
    pages.find((page) => textOf(page, 'pageCode').includes('CITY'));
  const defaultPage =
    pageId === 'ADM-D06'
      ? cityPage
      : ['ADM-D07', 'ADM-D08'].includes(pageId)
        ? productPage
        : pageId === 'ADM-D10'
          ? (productPage ??
            pages.find((page) => Number(page.currentPublishedVersionNumber ?? 0) > 0))
          : pages[0];
  const selectedPage = requestedPage ?? (isEditorPage ? {} : (defaultPage ?? pages[0] ?? {}));
  const selectedId = textOf(selectedPage, 'id');
  useEffect(() => {
    if (!isEditorPage) onPromotionTargetChange(selectedId === '—' ? '' : selectedId);
  }, [isEditorPage, selectedId, onPromotionTargetChange]);
  const versionsState = useApi(
    api,
    base && selectedId !== '—'
      ? `${base}/pages/${encodeURIComponent(selectedId)}/versions`
      : undefined,
  );
  const versions = rows(versionsState.data);
  const latest = versions[0] ?? {};
  const previewDraftState = useApi(
    api,
    pageId === 'ADM-D08' && base && selectedId !== '—'
      ? `${base}/pages/${encodeURIComponent(selectedId)}/draft`
      : undefined,
  );
  const [diffFromVersion, setDiffFromVersion] = useState('');
  const [diffToVersion, setDiffToVersion] = useState('');
  const effectiveDiffFrom =
    diffFromVersion || (versions[1] ? textOf(versions[1], 'versionNumber') : '');
  const effectiveDiffTo =
    diffToVersion || (versions[0] ? textOf(versions[0], 'versionNumber') : '');
  const diffState = useApi(
    api,
    pageId === 'ADM-D10' && base && selectedId !== '—' && effectiveDiffFrom && effectiveDiffTo
      ? `${base}/pages/${encodeURIComponent(selectedId)}/diff?fromVersion=${encodeURIComponent(effectiveDiffFrom)}&toVersion=${encodeURIComponent(effectiveDiffTo)}`
      : undefined,
  );
  const versionDiff = unwrapRecord(diffState.data);
  const diffSummary = nested(versionDiff, 'summary');
  const diffChanges = Array.isArray(versionDiff.changes)
    ? versionDiff.changes.filter(isRecord)
    : [];
  const snapshot = nested(latest, 'snapshot');
  const components = Array.isArray(snapshot.components) ? snapshot.components.filter(isRecord) : [];
  const previewProductId = textOf(selectedPage, 'productSpuId');
  const previewProductState = useApi(
    api,
    previewProductId !== '—'
      ? `/api/admin/m2/products/${encodeURIComponent(previewProductId)}`
      : undefined,
  );
  const previewProduct = unwrapRecord(previewProductState.data);
  const sourceTemplate = templates.find((template) => textOf(template, 'id') === copyTemplateId);
  const sourceCopyPage = pages.find((page) => textOf(page, 'id') === copyPageId);

  async function submitTemplateCopy() {
    if (!sourceTemplate) return;
    setCopyTemplateBusy(true);
    setCopyTemplateMessage('');
    try {
      await api.send(
        `${base}/templates/${encodeURIComponent(textOf(sourceTemplate, 'id'))}/copy`,
        'POST',
        {
          expectedVersion: Number(sourceTemplate.version ?? sourceTemplate.versionNumber ?? 0),
          templateCode: copyTemplateCode.trim(),
          name: copyTemplateName.trim(),
          reason: copyTemplateReason.trim(),
        },
      );
      setCopyTemplateMessage('模板副本已创建，结构与安全规则已重新校验');
      setCopyTemplateId('');
      setCopyTemplateCode('');
      setCopyTemplateName('');
      setCopyTemplateReason('');
      templatesState.reload();
    } catch (error) {
      setCopyTemplateMessage(operationError(error));
    } finally {
      setCopyTemplateBusy(false);
    }
  }

  async function submitPageCopy() {
    if (!sourceCopyPage) return;
    setCopyPageBusy(true);
    setCopyPageMessage('');
    try {
      await api.send(
        `${base}/pages/${encodeURIComponent(textOf(sourceCopyPage, 'id'))}/copy`,
        'POST',
        {
          targetAppId: copyTargetAppId,
          targetPageCode: copyPageCode.trim(),
          expectedDraftVersion: Number(sourceCopyPage.draftVersion ?? 0),
          reason: copyPageReason.trim(),
        },
      );
      const targetApp = cmsApps.find((app) => textOf(app, 'id') === copyTargetAppId);
      setCopyPageMessage(
        `页面副本已写入${clean(targetApp?.displayName, '目标小程序')}，组件和受控动作已重新校验`,
      );
      setCopyPageId('');
      setCopyPageCode('');
      setCopyPageReason('');
      if (copyTargetAppId === targetAppId) pagesState.reload();
    } catch (error) {
      setCopyPageMessage(operationError(error));
    } finally {
      setCopyPageBusy(false);
    }
  }
  if (!targetAppId)
    return <Empty noun="目标小程序" detail="请先在顶部选择目标小程序，再进入 CMS 配置。" />;
  if (['ADM-D02', 'ADM-D03', 'ADM-D04', 'ADM-D05'].includes(pageId))
    return (
      <CmsEditor
        onPromotionTargetChange={onPromotionTargetChange}
        api={api}
        appId={targetAppId}
        pageId={pageId}
        params={params}
        pages={pages}
        pagesState={pagesState}
        definitions={definitions}
        definitionsState={definitionsState}
      />
    );
  if (pageId === 'ADM-D01')
    return (
      <div className="r3-page r3-template-workspace">
        <Header
          title="页面模板列表"
          subtitle="内容装修（CMS）/ 页面模板 / 模板列表"
          actions={
            <button className="primary" onClick={() => setShowTemplateCreate((value) => !value)}>
              新建页面模板
            </button>
          }
        />
        {showTemplateCreate && (
          <Panel title="新建受控页面模板" hint="模板结构由系统规则约束，不允许脚本或任意接口">
            <div className="r3-field-list">
              <label>
                模板编码
                <input
                  value={templateCode}
                  maxLength={100}
                  placeholder="例如 CITY_HOME_V2"
                  onChange={(event) => setTemplateCode(event.target.value.toUpperCase())}
                />
              </label>
              <label>
                模板名称
                <input
                  value={templateName}
                  maxLength={150}
                  placeholder="请输入运营可识别的模板名称"
                  onChange={(event) => setTemplateName(event.target.value)}
                />
              </label>
              <label>
                模板说明
                <textarea
                  value={templateDescription}
                  maxLength={1000}
                  onChange={(event) => setTemplateDescription(event.target.value)}
                />
              </label>
            </div>
            <div className="r3-form-actions">
              <button type="button" onClick={() => setShowTemplateCreate(false)}>
                取消
              </button>
              <button
                type="button"
                className="primary"
                disabled={
                  templateSaving ||
                  !/^[A-Z][A-Z0-9_-]{2,99}$/u.test(templateCode) ||
                  !templateName.trim()
                }
                onClick={() => {
                  setTemplateSaving(true);
                  setTemplateMessage('');
                  void api
                    .send(`${base}/templates`, 'POST', {
                      templateCode,
                      name: templateName.trim(),
                      ...(templateDescription.trim()
                        ? { description: templateDescription.trim() }
                        : {}),
                      schema: {
                        schemaVersion: 1,
                        componentPolicy: 'SYSTEM_WHITELIST_ONLY',
                        actionPolicy: 'SYSTEM_WHITELIST_ONLY',
                      },
                    })
                    .then(() => {
                      setTemplateCode('');
                      setTemplateName('');
                      setTemplateDescription('');
                      setTemplateMessage('模板已创建');
                      templatesState.reload();
                    })
                    .catch((error: unknown) =>
                      setTemplateMessage(error instanceof Error ? error.message : '模板创建失败'),
                    )
                    .finally(() => setTemplateSaving(false));
                }}
              >
                {templateSaving ? '创建中…' : '创建模板'}
              </button>
            </div>
            {templateMessage && <Notice>{templateMessage}</Notice>}
          </Panel>
        )}
        {sourceTemplate && (
          <Panel title="复制页面模板" hint="复制后形成独立模板版本，系统会重新检查模板结构">
            <div className="r3-field-list">
              <Field
                name="源模板"
                value={clean(sourceTemplate.name ?? sourceTemplate.templateName, '未命名模板')}
              />
              <label>
                新模板编码
                <input
                  value={copyTemplateCode}
                  maxLength={100}
                  onChange={(event) => setCopyTemplateCode(event.target.value.toUpperCase())}
                />
              </label>
              <label>
                新模板名称
                <input
                  value={copyTemplateName}
                  maxLength={150}
                  onChange={(event) => setCopyTemplateName(event.target.value)}
                />
              </label>
              <label>
                复制原因
                <textarea
                  value={copyTemplateReason}
                  maxLength={1000}
                  onChange={(event) => setCopyTemplateReason(event.target.value)}
                />
              </label>
            </div>
            <div className="r3-form-actions">
              <button
                type="button"
                disabled={copyTemplateBusy}
                onClick={() => setCopyTemplateId('')}
              >
                取消
              </button>
              <button
                type="button"
                className="primary"
                disabled={
                  copyTemplateBusy ||
                  !/^[A-Z][A-Z0-9_-]{2,99}$/u.test(copyTemplateCode) ||
                  !copyTemplateName.trim() ||
                  !copyTemplateReason.trim()
                }
                onClick={() => void submitTemplateCopy()}
              >
                {copyTemplateBusy ? '复制中…' : '确认复制模板'}
              </button>
            </div>
          </Panel>
        )}
        {sourceCopyPage && (
          <Panel
            title="复制小程序页面"
            hint="可在同一小程序内复制，也可复制到已授权的小程序；私有凭据与任意接口地址不会被复制"
          >
            <div className="r3-field-list">
              <Field name="源页面" value={pageTitle(sourceCopyPage)} />
              <Field name="继承范围" value={label(sourceCopyPage.scopeType)} />
              <label>
                目标小程序
                <select
                  value={copyTargetAppId}
                  onChange={(event) => setCopyTargetAppId(event.target.value)}
                >
                  {cmsApps.map((app) => (
                    <option key={textOf(app, 'id')} value={textOf(app, 'id')}>
                      {clean(app.displayName, '未命名小程序')}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                目标页面编码
                <input
                  value={copyPageCode}
                  maxLength={100}
                  onChange={(event) => setCopyPageCode(event.target.value.toUpperCase())}
                />
              </label>
              <label>
                复制原因
                <textarea
                  value={copyPageReason}
                  maxLength={1000}
                  onChange={(event) => setCopyPageReason(event.target.value)}
                />
              </label>
            </div>
            <div className="canonical-rule-note">
              跨小程序复制会在目标小程序下重新验证城市/产品范围、组件
              结构规则、数据绑定与受控动作白名单。
            </div>
            <div className="r3-form-actions">
              <button type="button" disabled={copyPageBusy} onClick={() => setCopyPageId('')}>
                取消
              </button>
              <button
                type="button"
                className="primary"
                disabled={
                  copyPageBusy ||
                  !copyTargetAppId ||
                  !/^[A-Z][A-Z0-9_-]{2,99}$/u.test(copyPageCode) ||
                  !copyPageReason.trim()
                }
                onClick={() => void submitPageCopy()}
              >
                {copyPageBusy ? '复制中…' : '确认复制页面'}
              </button>
            </div>
          </Panel>
        )}
        {copyTemplateMessage && <Notice>{copyTemplateMessage}</Notice>}
        {copyPageMessage && <Notice>{copyPageMessage}</Notice>}
        <Panel
          title="页面模板列表"
          hint={`共 ${templates.length} 个模板 · ${pages.length} 个页面实例 · ${definitions.length} 个系统组件`}
          className="r3-cms-template-list"
        >
          <div className="r3-cms-list-toolbar">
            <span className="r3-locked-filter">全部页面类型</span>
            <label className="canonical-search">
              <span>搜索</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="请输入模板名称 / 页面名称"
              />
            </label>
            <button type="button" onClick={() => setQuery('')}>
              重置
            </button>
          </div>
          <State
            state={combineStates(templatesState, pagesState, definitionsState, appsState)}
            count={templates.length}
            noun="页面模板"
          >
            <Table
              heads={[
                '模板名称',
                '页面类型',
                '适用小程序',
                '城市覆盖',
                '当前版本',
                '状态',
                '更新时间',
                '操作',
              ]}
              empty={!templates.length}
            >
              {templates
                .filter((template) => {
                  const matchedPages = pages.filter(
                    (item) => textOf(item, 'templateId') === textOf(template, 'id'),
                  );
                  const haystack = [
                    clean(template.templateName ?? template.name, ''),
                    clean(template.templateCode, ''),
                    ...matchedPages.map(pageTitle),
                  ]
                    .join(' ')
                    .toLowerCase();
                  return !query || haystack.includes(query.toLowerCase());
                })
                .map((template) => {
                  const matchedPages = pages.filter(
                    (item) => textOf(item, 'templateId') === textOf(template, 'id'),
                  );
                  const firstPage = matchedPages[0];
                  const publishedCount = matchedPages.filter(
                    (page) => Number(page.currentPublishedVersionNumber ?? 0) > 0,
                  ).length;
                  return (
                    <tr key={textOf(template, 'id')}>
                      <td>
                        <strong>
                          {clean(template.templateName ?? template.name, '未命名模板')}
                        </strong>
                        <small>{clean(template.templateCode, '系统模板')}</small>
                      </td>
                      <td>{firstPage ? pageTypeLabel(firstPage) : '小程序页面'}</td>
                      <td>{cmsApps.length ? '全部小程序' : '当前小程序'}</td>
                      <td>
                        {matchedPages.some((page) => textOf(page, 'scopeType') === 'CITY')
                          ? '按城市覆盖'
                          : '无城市覆盖'}
                      </td>
                      <td>v{clean(template.versionNumber ?? template.version, '1')}</td>
                      <td>
                        <Pill value={template.status} />
                      </td>
                      <td>{dateTime(template.updatedAt ?? template.createdAt)}</td>
                      <td>
                        <span className="r3-row-actions">
                          {firstPage ? (
                            <>
                              <a href={pathFor('ADM-D08', { pageId: textOf(firstPage, 'id') })}>
                                预览
                              </a>
                              <a href={pathFor('ADM-D02', { pageId: textOf(firstPage, 'id') })}>
                                编辑
                              </a>
                            </>
                          ) : (
                            <span className="r3-muted">暂无页面</span>
                          )}
                          <button
                            className="r3-link-button"
                            type="button"
                            onClick={() => {
                              const code = clean(template.templateCode, 'TEMPLATE');
                              setCopyTemplateId(textOf(template, 'id'));
                              setCopyTemplateCode(`${code}_COPY`.toUpperCase().slice(0, 100));
                              setCopyTemplateName(
                                `${clean(template.name ?? template.templateName, '页面模板')}副本`,
                              );
                              setCopyTemplateReason('');
                              setCopyTemplateMessage('');
                            }}
                          >
                            复制
                          </button>
                          {firstPage && (
                            <a href={pathFor('ADM-D10', { id: textOf(firstPage, 'id') })}>
                              版本差异
                            </a>
                          )}
                        </span>
                        <small>
                          {matchedPages.length} 个实例 / 已发布 {publishedCount}
                        </small>
                      </td>
                    </tr>
                  );
                })}
            </Table>
            {visiblePages.length > 0 && (
              <div className="r3-cms-instance-strip">
                <span>
                  页面实例：{visiblePages.slice(0, 6).map(pageTitle).join('、')}
                  {visiblePages.length > 6 ? `等 ${visiblePages.length} 个` : ''}
                </span>
              </div>
            )}
          </State>
        </Panel>
        <Panel
          title="小程序页面实例"
          hint={`当前筛选 ${visiblePages.length} 个`}
          className="r3-cms-instance-panel"
        >
          <State state={pagesState} count={visiblePages.length} noun="页面实例">
            <Table
              heads={['页面', '覆盖范围', '版本', '状态', '操作']}
              empty={!visiblePages.length}
            >
              {visiblePages.map((page) => (
                <tr key={textOf(page, 'id')}>
                  <td>
                    <strong>{pageTitle(page)}</strong>
                    <small>小程序内容页面</small>
                  </td>
                  <td>{label(page.scopeType)}</td>
                  <td>V{clean(page.currentPublishedVersionNumber, '尚未发布')}</td>
                  <td>
                    <Pill value={page.status} />
                  </td>
                  <td>
                    <span className="r3-row-actions">
                      <a href={pathFor('ADM-D02', { pageId: textOf(page, 'id') })}>编辑</a>
                      <button
                        className="r3-link-button"
                        type="button"
                        onClick={() => {
                          setCopyPageId(textOf(page, 'id'));
                          setCopyTargetAppId(targetAppId);
                          setCopyPageCode(`${clean(page.pageCode, 'PAGE')}_COPY`.slice(0, 100));
                          setCopyPageReason('');
                          setCopyPageMessage('');
                        }}
                      >
                        复制
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </Table>
          </State>
        </Panel>
      </div>
    );
  if (pageId === 'ADM-D06' || pageId === 'ADM-D07') {
    const cityCoverage = pageId === 'ADM-D06';
    const scope = cityCoverage ? 'CITY' : 'PRODUCT';
    const scoped = pages.filter((page) => textOf(page, 'scopeType') === scope);
    const activeScopedPage = scoped.find((page) => textOf(page, 'id') === selectedId) ?? scoped[0];
    const activeId = textOf(activeScopedPage ?? {}, 'id');
    return (
      <div className="r3-page r3-coverage-workspace">
        <Header
          title={cityCoverage ? '城市装修覆盖' : '产品详情模板'}
          subtitle={
            cityCoverage ? '维护各城市独立的页面内容与发布版本' : '维护产品详情页面的组件与展示内容'
          }
          actions={
            <a className="r3-button-link secondary" href={pathFor('ADM-D01')}>
              全部页面
            </a>
          }
        />
        <State
          state={pagesState}
          count={scoped.length}
          noun={cityCoverage ? '城市覆盖页面' : '产品详情页面'}
        >
          <div className="r3-side-layout r3-cms-coverage-layout">
            <Panel title={cityCoverage ? '城市装修覆盖' : '产品详情页面'}>
              <Table
                heads={['页面', '当前版本', '草稿版本', '状态', '操作']}
                empty={!scoped.length}
              >
                {scoped.map((page) => (
                  <tr
                    key={textOf(page, 'id')}
                    className={textOf(page, 'id') === activeId ? 'r3-selected-record' : ''}
                  >
                    <td>
                      <strong>{pageTitle(page)}</strong>
                    </td>
                    <td>
                      {Number(page.currentPublishedVersionNumber) > 0
                        ? `V${page.currentPublishedVersionNumber}`
                        : '尚未发布'}
                    </td>
                    <td>V{clean(page.draftVersion, '0')}</td>
                    <td>
                      <Pill value={page.status} />
                    </td>
                    <td>
                      <span className="r3-row-actions">
                        <button type="button" onClick={() => setPreviewPageId(textOf(page, 'id'))}>
                          查看配置
                        </button>
                        <a href={pathFor('ADM-D03', { pageId: textOf(page, 'id') })}>编辑</a>
                        <a
                          href={`${pathFor('ADM-D08')}?pageId=${encodeURIComponent(textOf(page, 'id'))}`}
                        >
                          预览
                        </a>
                      </span>
                    </td>
                  </tr>
                ))}
              </Table>
            </Panel>
            {activeScopedPage && (
              <Panel
                title={pageTitle(activeScopedPage)}
                actions={<a href={pathFor('ADM-D10', { id: activeId })}>版本差异</a>}
              >
                <div className="r3-field-list">
                  <Field name="覆盖层级" value={label(activeScopedPage.scopeType)} />
                  <Field name="发布状态" value={<Pill value={activeScopedPage.status} />} />
                </div>
                <State state={versionsState} count={versions.length} noun="页面版本">
                  <div className="r3-component-options r3-component-facts">
                    {components.map((component, index) => (
                      <div key={`${textOf(component, 'instanceKey')}-${index}`}>
                        <strong>
                          {index + 1}.{' '}
                          {componentLabel(component.displayName ?? component.componentKey)}
                        </strong>
                        <small>{componentActionSummary(component.actionIds)}</small>
                      </div>
                    ))}
                    {!components.length && <Empty noun="版本组件" />}
                  </div>
                </State>
                <a className="r3-button-link" href={pathFor('ADM-D03', { pageId: activeId })}>
                  维护页面组件
                </a>
                <p className="canonical-rule-note">
                  页面装修仅管理显示内容；线路价格和预订信息在线路管理中维护。线路默认每天可售，停售请下架，退款由客服人工审核。
                </p>
              </Panel>
            )}
          </div>
        </State>
      </div>
    );
  }
  if (pageId === 'ADM-D08') {
    const previewDraft = unwrapRecord(previewDraftState.data);
    const previewComponents: JsonRecord[] = nestedRows(previewDraft, 'components').map(
      (component) => ({
        ...component,
        componentKey:
          component.componentKey ?? nested(component, 'componentDefinition').componentKey,
      }),
    );
    const previewStates = [previewDraftState, definitionsState];
    if (previewProductId !== '—') previewStates.push(previewProductState);
    const previewState = combineStates(...previewStates);
    const definitionByComponent = new Map(
      definitions.map((definition) => [textOf(definition, 'componentKey'), definition]),
    );
    const definitionsReady =
      !definitionsState.loading && !definitionsState.error && definitions.length > 0;
    const componentRulesPassed =
      Array.isArray(previewDraft.components) &&
      definitionsReady &&
      previewComponents.every((component) =>
        definitionByComponent.has(textOf(component, 'componentKey')),
      );
    const actionRulesPassed =
      componentRulesPassed &&
      previewComponents.every((component) => {
        const definition = definitionByComponent.get(textOf(component, 'componentKey'));
        const allowed = new Set(stringList(definition?.allowedActionIds));
        return stringList(component.actionIds).every((actionId) => allowed.has(actionId));
      });
    const isolationPassed = selectedId !== '—';
    return (
      <div className="r3-page r3-preview-workspace">
        <Header
          title="装修预览"
          subtitle="内容装修（CMS）/ 装修预览 / 线路详情模板"
          actions={<span className="r3-closed-state">草稿预览</span>}
        />
        <State state={pagesState} count={pages.length} noun="可预览页面">
          <div className="r3-preview-layout">
            <Panel title="预览条件" hint="按小程序、城市、页面、产品与版本生成预览">
              <div className="r3-selection-list">
                {pages.map((page) => (
                  <button
                    type="button"
                    key={textOf(page, 'id')}
                    className={textOf(page, 'id') === selectedId ? 'active' : ''}
                    onClick={() => setPreviewPageId(textOf(page, 'id'))}
                  >
                    {pageTitle(page)}
                  </button>
                ))}
              </div>
              <div className="r3-field-list">
                <Field name="小程序" value="当前所选小程序" />
                <Field name="城市" value={pageTitle(selectedPage).replace('城市首页', '')} />
                <Field
                  name="页面"
                  value={selectedId !== '—' ? pageTitle(selectedPage) : '尚未选择'}
                />
                <Field
                  name="产品 / SKU"
                  value={clean(previewProduct.title, '按页面绑定产品加载')}
                />
                <Field
                  name="版本"
                  value={
                    selectedId !== '—'
                      ? `草稿 V${clean(previewDraft.draftVersion, '尚无草稿')}`
                      : '—'
                  }
                />
              </div>
              <button
                type="button"
                className="canonical-primary"
                onClick={() => {
                  previewDraftState.reload();
                  previewProductState.reload();
                }}
              >
                刷新预览
              </button>
              <a
                className="r3-button-link secondary"
                href={pathFor('ADM-D03', { pageId: selectedId })}
              >
                返回组件配置
              </a>
            </Panel>
            {selectedId !== '—' ? (
              <State
                state={previewState}
                count={Array.isArray(previewDraft.components) ? 1 : 0}
                noun="页面草稿"
              >
                <Panel
                  title="装修预览"
                  hint="已保存草稿的展示效果"
                  actions={<span className="r3-closed-state">非发布动作</span>}
                >
                  <CmsPhone
                    components={previewComponents}
                    title={pageTitle(selectedPage)}
                    product={previewProduct}
                    variant={textOf(selectedPage, 'scopeType') === 'PRODUCT' ? 'detail' : 'catalog'}
                  />
                </Panel>
              </State>
            ) : (
              <Empty noun="待预览页面" detail="请先从左侧选择一个页面。" />
            )}
            <Panel
              title="发布前检查"
              actions={
                <span className="canonical-pill green">
                  {
                    [componentRulesPassed, actionRulesPassed, isolationPassed].filter(Boolean)
                      .length
                  }
                  / 3 项已检查
                </span>
              }
            >
              <div className="r3-checklist">
                <div>
                  <span>{componentRulesPassed ? '✓' : '○'}</span>
                  <strong>页面结构合法</strong>
                  <small>
                    {componentRulesPassed ? '草稿组件与系统定义匹配' : '选择有效草稿后检查'}
                  </small>
                </div>
                <div>
                  <span>{actionRulesPassed ? '✓' : '○'}</span>
                  <strong>动作安全</strong>
                  <small>
                    {actionRulesPassed
                      ? '当前版本中的全部操作均属于对应组件白名单'
                      : definitionsState.loading
                        ? '正在加载系统操作定义'
                        : !definitions.length
                          ? '系统尚未返回可用操作定义'
                          : '当前版本存在未识别组件或非白名单操作'}
                  </small>
                </div>
                <div>
                  <span>{isolationPassed ? '✓' : '○'}</span>
                  <strong>多小程序隔离</strong>
                  <small>{isolationPassed ? '页面属于当前小程序' : '尚未选择页面'}</small>
                </div>
              </div>
              <p className="canonical-rule-note">
                发布时由服务端重新校验。真机效果与支付流程请在微信开发者工具或体验版中验收。
              </p>
            </Panel>
          </div>
        </State>
      </div>
    );
  }
  if (pageId === 'ADM-D09')
    return (
      <div className="r3-page">
        <Header
          title="装修发布版本"
          subtitle="内容装修（CMS）/ 装修发布 / 版本管理"
          actions={
            <>
              <button
                type="button"
                onClick={() => {
                  pagesState.reload();
                  versionsState.reload();
                }}
              >
                刷新版本
              </button>
            </>
          }
        />
        <div className="r3-side-layout r3-release-layout">
          <Panel
            title="装修发布版本"
            hint="每次发布形成不可变版本，可作为后续回滚点"
            actions={
              <>
                <span className="r3-locked-filter">当前小程序</span>
                <span className="r3-locked-filter">全部页面</span>
              </>
            }
          >
            <State state={pagesState} count={pages.length} noun="发布页面">
              <Table
                heads={['版本', '范围', '变更摘要', '提交人', '发布时间', '状态', '操作']}
                empty={!pages.length}
              >
                {pages.map((page) => (
                  <tr key={textOf(page, 'id')}>
                    <td>
                      <strong>v{clean(page.currentPublishedVersionNumber, '1.0')}</strong>
                      <small>{pageTitle(page)}</small>
                    </td>
                    <td>{label(page.scopeType)}</td>
                    <td>{clean(page.changeSummary, '查看版本差异')}</td>
                    <td>{clean(page.updatedByName ?? page.createdByName, '未记录')}</td>
                    <td>{dateTime(page.updatedAt ?? page.createdAt)}</td>
                    <td>
                      <Pill value={page.status} />
                    </td>
                    <td>
                      <span className="r3-row-actions">
                        <a href={pathFor('ADM-D10', { id: textOf(page, 'id') })}>查看差异</a>
                        <button type="button" onClick={() => setPreviewPageId(textOf(page, 'id'))}>
                          选择发布
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </Table>
            </State>
          </Panel>
          {selectedId !== '—' ? (
            <Panel
              title={`待发布：${pageTitle(selectedPage)}`}
              hint="提交时执行版本冲突、幂等和审计校验"
            >
              <div className="r3-field-list">
                <Field name="影响范围" value={label(selectedPage.scopeType)} />
                <Field
                  name="当前线上"
                  value={`V${clean(selectedPage.currentPublishedVersionNumber, '尚未发布')}`}
                />
                <Field name="组件检查" value={`${components.length} 个版本组件`} />
                <Field name="动作链" value="发布时重新校验白名单" />
                <Field name="自动回滚点" value="保留上一已发布版本" />
              </div>
              <CmsReleaseCommand
                key={`${selectedId}-${clean(selectedPage.currentPublishedVersionNumber, '0')}`}
                api={api}
                path={`${base}/pages/${encodeURIComponent(selectedId)}/publish`}
                expectedVersion={Number(selectedPage.currentPublishedVersionNumber ?? 0)}
                action="发布"
                onDone={() => {
                  pagesState.reload();
                  versionsState.reload();
                }}
              />
            </Panel>
          ) : (
            <Panel title="待发布版本">
              <Empty noun="可发布页面" />
            </Panel>
          )}
        </div>
      </div>
    );
  const fromVersionRecord =
    versions.find((version) => textOf(version, 'versionNumber') === effectiveDiffFrom) ?? {};
  const toVersionRecord =
    versions.find((version) => textOf(version, 'versionNumber') === effectiveDiffTo) ?? {};
  const fromVersionSnapshot = nested(fromVersionRecord, 'snapshot');
  const toVersionSnapshot = nested(toVersionRecord, 'snapshot');
  const fromComponentCount = Array.isArray(fromVersionSnapshot.components)
    ? (fromVersionSnapshot.components as unknown[]).length
    : 0;
  const toComponentCount =
    toVersionRecord.id && Array.isArray(toVersionSnapshot.components)
      ? (toVersionSnapshot.components as unknown[]).length
      : 0;
  const fallbackDiffChanges =
    diffChanges.length === 0 &&
    fromVersionRecord.id &&
    toVersionRecord.id &&
    fromComponentCount !== toComponentCount
      ? [
          {
            path: 'components.count',
            kind: 'CHANGED',
            risk: 'LOW',
            before: fromComponentCount,
            after: toComponentCount,
          },
        ]
      : [];
  const visibleDiffChanges = diffChanges.length ? diffChanges : fallbackDiffChanges;
  const visibleDiffTotal = fallbackDiffChanges.length
    ? String(fallbackDiffChanges.length)
    : clean(diffSummary.total, diffState.loading ? '计算中…' : '0');
  const visibleHighRisk = fallbackDiffChanges.length ? '0' : clean(diffSummary.highRisk, '0');
  return (
    <div className="r3-page">
      <Header
        title="装修差异与回滚"
        subtitle="内容装修（CMS）/ 版本差异与回滚 / 差异对比"
        actions={<span className="r3-closed-state">仅回滚页面装修配置</span>}
      />
      <State state={versionsState} count={versions.length} noun="页面版本">
        <Panel title="装修差异与回滚" hint="差异由服务端基于不可变发布快照计算">
          <div className="r3-filter-row">
            <label>
              基线版本
              <select
                value={effectiveDiffFrom}
                onChange={(event) => setDiffFromVersion(event.target.value)}
              >
                {versions.map((version) => (
                  <option key={textOf(version, 'id')} value={textOf(version, 'versionNumber')}>
                    V{clean(version.versionNumber)} · {dateTime(version.publishedAt)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              目标版本
              <select
                value={effectiveDiffTo}
                onChange={(event) => setDiffToVersion(event.target.value)}
              >
                {versions.map((version) => (
                  <option key={textOf(version, 'id')} value={textOf(version, 'versionNumber')}>
                    V{clean(version.versionNumber)} · {dateTime(version.publishedAt)}
                  </option>
                ))}
              </select>
            </label>
            <Field name="差异项" value={visibleDiffTotal} />
            <Field name="高风险变化" value={visibleHighRisk} />
          </div>
        </Panel>
        <div className="r3-version-layout">
          <div className="r3-stack">
            <Panel title="版本内容对比" hint="对比数据来自不可变的服务端版本快照">
              <div className="r3-detail-grid">
                <article className="r3-version-card baseline">
                  <header>
                    <strong>V{clean(fromVersionRecord.versionNumber, '—')} · 当前线上</strong>
                    <span className="canonical-pill green">基线</span>
                  </header>
                  <div className="r3-field-list">
                    <Field name="页面" value={pageTitle(selectedPage)} />
                    <Field
                      name="组件数量"
                      value={
                        Array.isArray(nested(fromVersionRecord, 'snapshot').components)
                          ? fromComponentCount
                          : 0
                      }
                    />
                    <Field name="发布时间" value={dateTime(fromVersionRecord.publishedAt)} />
                    <Field
                      name="版本备注"
                      value={displayOperationNote(fromVersionRecord.operationNote, '稳定线上版本')}
                    />
                  </div>
                </article>
                <article className="r3-version-card changed">
                  <header>
                    <strong>V{clean(toVersionRecord.versionNumber, '—')} · 目标版本</strong>
                    {toVersionRecord.id ? (
                      <span className="canonical-pill orange">
                        {clean(diffSummary.total, '0')} 项变化
                      </span>
                    ) : (
                      <span className="r3-muted">未选择版本</span>
                    )}
                  </header>
                  <div className="r3-field-list">
                    <Field
                      name="页面"
                      value={toVersionRecord.id ? pageTitle(selectedPage) : '暂无可对比版本'}
                    />
                    <Field
                      name="组件数量"
                      value={
                        toVersionRecord.id
                          ? Array.isArray(toVersionSnapshot.components)
                            ? toComponentCount
                            : 0
                          : '—'
                      }
                    />
                    <Field name="发布时间" value={dateTime(toVersionRecord.publishedAt)} />
                    <Field name="高风险变化" value={`${visibleHighRisk} 项（组件或动作）`} />
                  </div>
                </article>
              </div>
            </Panel>
            <Panel title="字段级差异" hint="优先展示服务端字段差异；缺省时根据版本快照补充结构摘要">
              <State state={diffState} count={visibleDiffChanges.length} noun="版本差异">
                <Table
                  heads={['对象', '变化', '基线值', '目标值', '风险']}
                  empty={!visibleDiffChanges.length}
                >
                  {visibleDiffChanges.map((change, index) => (
                    <tr key={`${clean(change.path)}-${index}`}>
                      <td>{diffPathLabel(change.path)}</td>
                      <td>{label(change.kind)}</td>
                      <td>{displayDiffValue(change.before)}</td>
                      <td>{displayDiffValue(change.after)}</td>
                      <td>
                        <Pill value={change.risk} />
                      </td>
                    </tr>
                  ))}
                </Table>
              </State>
            </Panel>
          </div>
          <Panel title="回滚控制" hint="仅回滚页面装修配置，不改变交易事实">
            <div className="r3-release-summary-cards">
              <article>
                <strong>当前线上</strong>
                <span>V{clean(selectedPage.currentPublishedVersionNumber, '尚未发布')}</span>
              </article>
              <article>
                <strong>可回滚版本</strong>
                <span>{versions[1] ? `V${clean(versions[1].versionNumber)}` : '暂无'}</span>
              </article>
            </div>
            <div className="r3-field-list">
              <Field
                name="当前线上"
                value={`V${clean(selectedPage.currentPublishedVersionNumber, '尚未发布')}`}
              />
              <Field
                name="可回滚版本"
                value={versions[1] ? `V${clean(versions[1].versionNumber)}` : '暂无'}
              />
            </div>
            <div className="r3-checklist compact">
              <div>
                <span>✓</span>
                <strong>不影响订单交易数据</strong>
                <small>只恢复 CMS 页面版本</small>
              </div>
              <div>
                <span>✓</span>
                <strong>不改变产品与套餐数据</strong>
                <small>产品事实仍由产品域维护</small>
              </div>
              <div>
                <span>✓</span>
                <strong>强制记录审计</strong>
                <small>原因、操作者、版本和请求链路均持久化</small>
              </div>
            </div>
            {versions[1] && (
              <CmsReleaseCommand
                key={`${selectedId}-${clean(versions[0]?.versionNumber, '0')}-${clean(versions[1]?.versionNumber, '0')}`}
                api={api}
                path={`${base}/pages/${encodeURIComponent(selectedId)}/rollback/${encodeURIComponent(textOf(versions[1], 'versionNumber'))}`}
                expectedVersion={Number(selectedPage.currentPublishedVersionNumber ?? 0)}
                action="回滚"
                onDone={() => {
                  pagesState.reload();
                  versionsState.reload();
                }}
              />
            )}
          </Panel>
        </div>
      </State>
    </div>
  );
}

function orderNo(order: JsonRecord) {
  return clean(order.orderNo, '订单号暂未生成');
}

function OrderTable({
  api,
  appId,
  orders,
  selected,
  onSelect,
}: {
  api: AdminApiClient;
  appId: string;
  orders: JsonRecord[];
  selected: string[];
  onSelect: (ids: string[]) => void;
}) {
  return (
    <Table
      heads={[
        '选择',
        '订单号 / 支付',
        '产品 / 套餐',
        '来源小程序 / 城市',
        '出行日期',
        '金额账目',
        '状态 / 处理人',
        '操作',
      ]}
      empty={!orders.length}
    >
      {orders.map((order) => {
        const trip = nested(order, 'tripInfo');
        return (
          <tr
            key={textOf(order, 'id')}
            className={selected.includes(textOf(order, 'id')) ? 'order-selected' : ''}
          >
            <td className="order-selection" title={orderSelectionBlocked(order) || '选择订单'}>
              <input
                type="checkbox"
                aria-label={`选择订单 ${orderNo(order)}`}
                disabled={
                  Boolean(orderSelectionBlocked(order)) ||
                  (!selected.includes(textOf(order, 'id')) && selected.length >= 50)
                }
                checked={selected.includes(textOf(order, 'id'))}
                onChange={(event) =>
                  onSelect(
                    event.target.checked
                      ? [...selected, textOf(order, 'id')]
                      : selected.filter((id) => id !== textOf(order, 'id')),
                  )
                }
              />
            </td>
            <td>
              <strong>{orderNo(order)}</strong>
              <small>
                {paymentLabel(order.paymentStatus)} · {label(order.orderMode, '统一订单')}
              </small>
            </td>
            <td>
              <strong>
                {clean(order.productName ?? order.productTitle, '产品信息按权限裁剪')}
              </strong>
              <small>{clean(order.skuName ?? order.skuTitle, '套餐信息按权限裁剪')}</small>
            </td>
            <td>
              <strong>
                {clean(
                  order.sourceAppName ??
                    order.sourceName ??
                    order.appName ??
                    unwrapRecord(order.app).displayName,
                  '来源小程序未标注',
                )}
              </strong>
              <small>
                {clean(order.cityName, '城市信息未提供')} ·{' '}
                {clean(order.channelName, '自然访问或未标记')}
              </small>
            </td>
            <td>{dateOnly(trip.travelDate ?? order.travelDate)}</td>
            <td>
              <strong>应付 {protectedMoney(order.payAmountCents ?? order.amountCents)}</strong>
              <small>
                原价 {protectedMoney(order.grossAmountCents)} · 优惠{' '}
                {typeof order.discountAmountCents === 'number'
                  ? `-${money(order.discountAmountCents)}`
                  : '无权限查看'}
              </small>
              <small>实付 {protectedMoney(order.actualPaidAmountCents)}</small>
            </td>
            <td>
              <Pill value={order.status} />
              <small>{clean(order.assigneeName, '未分配')}</small>
            </td>
            <td className="order-row-actions">
              <a href={pathFor('ADM-E02', { id: textOf(order, 'id') })}>查看订单</a>
              <CopyOrderButton
                key={`${appId}:${textOf(order, 'id')}`}
                api={api}
                appId={appId}
                orderId={textOf(order, 'id')}
              />
            </td>
          </tr>
        );
      })}
    </Table>
  );
}

function ConfirmationQueue({
  api,
  appId,
  state,
  orders,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  orders: JsonRecord[];
}) {
  return (
    <Table heads={['订单号', '关键资料', '当前状态', '剩余处理', '操作']} empty={!orders.length}>
      {orders.map((order) => (
        <ConfirmationRow
          key={textOf(order, 'id')}
          api={api}
          appId={appId}
          order={order}
          onDone={state.reload}
        />
      ))}
    </Table>
  );
}

function ConfirmationRow({
  api,
  appId,
  order,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  order: JsonRecord;
  onDone: () => void;
}) {
  const trip = nested(order, 'tripInfo');
  const complete = Boolean(trip.travelDate && trip.peopleCount && trip.contactName && trip.mobile);
  const correctState = textOf(order, 'status') === 'PENDING_CONFIRM';
  const [confirmed, setConfirmed] = useState(false);
  return (
    <tr>
      <td>
        <strong>{orderNo(order)}</strong>
      </td>
      <td>
        <span className={`canonical-pill ${complete ? 'green' : 'orange'}`}>
          {complete ? '资料完整' : '资料待核验'}
        </span>
        <small>
          {dateOnly(trip.travelDate)} · {clean(trip.peopleCount, '人数待补充')}人
        </small>
      </td>
      <td>
        <Pill value={order.status} />
      </td>
      <td>
        <label className="r3-inline-check">
          <input
            type="checkbox"
            checked={confirmed}
            disabled={!complete || !correctState}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          已核对日期、人数与接送信息
        </label>
      </td>
      <td>
        <span className="r3-row-actions">
          <a href={pathFor('ADM-E02', { id: textOf(order, 'id') })}>查看</a>
          <MutationButton
            api={api}
            path={`/api/admin/m4/orders/${encodeURIComponent(textOf(order, 'id'))}/transitions?appId=${encodeURIComponent(appId)}`}
            body={{ command: 'CONFIRM_ORDER', expectedVersion: Number(order.version ?? 0) }}
            idempotent
            disabled={!complete || !correctState || !confirmed}
            label="确认订单"
            onDone={onDone}
          />
        </span>
      </td>
    </tr>
  );
}

function FulfillmentForm({
  api,
  appId,
  order,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  order: JsonRecord;
  onDone: () => void;
}) {
  const fulfillment = nested(order, 'fulfillment');
  const checklist = nestedRows(fulfillment, 'checklist');
  const [vehicleDetail, setVehicle] = useState(clean(fulfillment.vehicleDetail, ''));
  const [guideDetail, setGuide] = useState(clean(fulfillment.guideDetail, ''));
  const [ticketDetail, setTicket] = useState(clean(fulfillment.ticketDetail, ''));
  const [pickupTime, setPickup] = useState(clean(fulfillment.pickupTime, ''));
  const [serviceRemark, setRemark] = useState(clean(fulfillment.serviceRemark, ''));
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(checklist.map((item) => [clean(item.code), Boolean(item.completed)])),
  );
  const body = {
    expectedVersion: Number(fulfillment.version ?? 0),
    vehicleDetail,
    guideDetail,
    ticketDetail,
    pickupTime,
    serviceRemark,
    checklist: checklist.map((item) => ({
      code: clean(item.code),
      completed: Boolean(checked[clean(item.code)]),
      ...(typeof item.note === 'string' ? { note: item.note } : {}),
    })),
  };
  return (
    <div className="r3-fulfillment-form">
      <div className="r3-form-grid">
        <label>
          车辆 / 司机
          <input value={vehicleDetail} onChange={(event) => setVehicle(event.target.value)} />
        </label>
        <label>
          向导 / 服务人员
          <input value={guideDetail} onChange={(event) => setGuide(event.target.value)} />
        </label>
        <label>
          票务处理
          <input value={ticketDetail} onChange={(event) => setTicket(event.target.value)} />
        </label>
        <label>
          最终出发时间
          <input value={pickupTime} onChange={(event) => setPickup(event.target.value)} />
        </label>
        <label className="wide">
          运营备注
          <textarea value={serviceRemark} onChange={(event) => setRemark(event.target.value)} />
        </label>
      </div>
      <div className="r3-checklist editable">
        {checklist.map((item) => {
          const code = clean(item.code);
          return (
            <label key={code}>
              <input
                type="checkbox"
                checked={Boolean(checked[code])}
                onChange={(event) =>
                  setChecked((current) => ({ ...current, [code]: event.target.checked }))
                }
              />
              <strong>{checklistLabels[code] ?? '履约检查项'}</strong>
            </label>
          );
        })}
      </div>
      <MutationButton
        api={api}
        path={`/api/admin/m4/orders/${encodeURIComponent(textOf(order, 'id'))}/fulfillment?appId=${encodeURIComponent(appId)}`}
        method="PUT"
        body={body}
        idempotent
        label="保存履约事实"
        onDone={onDone}
      />
    </div>
  );
}

function CommunicationForm({
  api,
  appId,
  orderId,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  orderId: string;
  onDone: () => void;
}) {
  const [channel, setChannel] = useState('PHONE'),
    [direction, setDirection] = useState('OUTBOUND'),
    [summary, setSummary] = useState('');
  return (
    <form className="r3-communication-form" onSubmit={(event) => event.preventDefault()}>
      <label>
        记录类型
        <select value={channel} onChange={(event) => setChannel(event.target.value)}>
          <option value="PHONE">电话</option>
          <option value="WECHAT">企业微信</option>
          <option value="IN_APP">站内消息</option>
          <option value="OTHER">其他</option>
        </select>
      </label>
      <label>
        沟通方向
        <select value={direction} onChange={(event) => setDirection(event.target.value)}>
          <option value="OUTBOUND">主动联系</option>
          <option value="INBOUND">用户来电</option>
          <option value="INTERNAL">内部备注</option>
        </select>
      </label>
      <label className="wide">
        沟通结果
        <textarea
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          placeholder="仅记录必要业务摘要，禁止写入证件号、密码或密钥"
        />
      </label>
      <MutationButton
        api={api}
        path={`/api/admin/m4/orders/${encodeURIComponent(orderId)}/communications?appId=${encodeURIComponent(appId)}`}
        body={{ channel, direction, summary }}
        disabled={!summary.trim() || orderId === '—'}
        label="保存沟通记录"
        onDone={() => {
          setSummary('');
          onDone();
        }}
      />
    </form>
  );
}

function OrderDetail({
  api,
  appId,
  pageId,
  orderState,
  order,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  orderState: LoadState;
  order: JsonRecord;
}) {
  const orderId = textOf(order, 'id');
  const [detailTab, setDetailTab] = useState('出行资料');
  useEffect(() => {
    setDetailTab('出行资料');
  }, [orderId, appId]);
  const fulfillment = nested(order, 'fulfillment'),
    trip = nested(order, 'tripInfo');
  const orderStatus = textOf(order, 'status');
  const checklist = nestedRows(fulfillment, 'checklist');
  const fulfillmentComplete = checklist.length > 0 && checklist.every((item) => item.completed);
  const transitionPath = `/api/admin/m4/orders/${encodeURIComponent(orderId)}/transitions?appId=${encodeURIComponent(appId)}`;
  const communications = useApi(
    api,
    orderId !== '—' && ['ADM-E02', 'ADM-E07'].includes(pageId)
      ? `/api/admin/m4/orders/${encodeURIComponent(orderId)}/communications?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const communicationRows = rows(communications.data);
  if (pageId === 'ADM-E06')
    return (
      <div className="r3-page order-list-first order-fulfillment-page">
        <Header
          title="人工履约回填"
          subtitle={`订单 ${orderNo(order)} · 人工事实、状态与审计留痕`}
          actions={
            <a className="r3-button-link secondary" href={pathFor('ADM-E02', { id: orderId })}>
              返回订单详情
            </a>
          }
        />
        <State state={orderState} count={Object.keys(order).length} noun="订单">
          <div className="r3-side-layout">
            <div className="r3-stack">
              <Panel title="服务资源（人工回填）">
                {orderStatus === 'CONFIRMED' ? (
                  <MutationButton
                    api={api}
                    path={transitionPath}
                    body={{
                      command: 'START_FULFILLMENT_PREPARATION',
                      expectedVersion: Number(order.version ?? 0),
                    }}
                    idempotent
                    label="开始履约准备"
                    onDone={orderState.reload}
                  />
                ) : orderStatus === 'PENDING_FULFILLMENT' ? (
                  <FulfillmentForm
                    api={api}
                    appId={appId}
                    order={order}
                    onDone={orderState.reload}
                  />
                ) : (
                  <div className="canonical-unavailable compact">
                    <span>i</span>
                    <div>
                      <strong>当前状态不可编辑履约资料</strong>
                      <p>请按订单状态顺序完成前置步骤。</p>
                    </div>
                  </div>
                )}
              </Panel>
            </div>
            <Panel title="履约状态门禁" hint="系统不会自动派车或自动出票">
              <div className="r3-field-list">
                <Field name="当前状态" value={<Pill value={order.status} />} />
                <Field name="出行日期" value={dateOnly(trip.travelDate)} />
                <Field name="接送地址" value={clean(trip.pickupAddress, '受字段权限保护')} />
                <Field name="履约版本" value={clean(fulfillment.version, '尚未建立')} />
              </div>
              {orderStatus === 'PENDING_FULFILLMENT' && (
                <MutationButton
                  api={api}
                  path={transitionPath}
                  body={{
                    command: 'MARK_FULFILLMENT_READY',
                    expectedVersion: Number(order.version ?? 0),
                  }}
                  idempotent
                  disabled={!fulfillmentComplete}
                  label="确认履约准备完成"
                  onDone={orderState.reload}
                />
              )}
              {orderStatus === 'FULFILLMENT_READY' && (
                <MutationButton
                  api={api}
                  path={transitionPath}
                  body={{ command: 'START_SERVICE', expectedVersion: Number(order.version ?? 0) }}
                  idempotent
                  label="开始服务"
                  onDone={orderState.reload}
                />
              )}
              {orderStatus === 'IN_SERVICE' && (
                <MutationButton
                  api={api}
                  path={transitionPath}
                  body={{
                    command: 'COMPLETE_SERVICE',
                    expectedVersion: Number(order.version ?? 0),
                  }}
                  idempotent
                  label="完成服务"
                  onDone={orderState.reload}
                />
              )}
            </Panel>
          </div>
        </State>
      </div>
    );
  if (pageId === 'ADM-E07')
    return (
      <div className="r3-page r3-communication-workspace order-list-first">
        <Header
          title="沟通日志与备注"
          subtitle={`订单 ${orderNo(order)} · 联系记录统一留痕`}
          actions={
            <a className="r3-button-link secondary" href={pathFor('ADM-E02', { id: orderId })}>
              返回订单详情
            </a>
          }
        />
        <State state={orderState} count={Object.keys(order).length} noun="订单">
          <div className="r3-record-strip">
            <span>
              <strong>{clean(order.productName, '按权限裁剪')}</strong>
            </span>
            <span>
              当前状态 <Pill value={order.status} />
            </span>
            <span>处理人 {clean(order.assigneeName, '未分配')}</span>
          </div>
          <div className="r3-communication-layout">
            <Panel title="新增沟通记录">
              <CommunicationForm
                api={api}
                appId={appId}
                orderId={orderId}
                onDone={communications.reload}
              />
            </Panel>
            <Panel title={`订单沟通日志 · ${orderNo(order)}`}>
              <State state={communications} count={communicationRows.length} noun="沟通记录">
                <div className="r3-timeline">
                  {communicationRows.map((item) => (
                    <article key={textOf(item, 'id')}>
                      <span />
                      <div>
                        <small>
                          {dateTime(item.createdAt)} · {label(item.channel)} ·{' '}
                          {label(item.direction)}
                        </small>
                        <strong>{clean(item.summary)}</strong>
                      </div>
                    </article>
                  ))}
                </div>
              </State>
            </Panel>
          </div>
        </State>
      </div>
    );
  const logs = [...nestedRows(order, 'statusLogs')].sort((a, b) =>
    String(b.createdAt).localeCompare(String(a.createdAt)),
  );
  const tabs = ['出行资料', '支付与退款', '履约处理', '沟通记录', '操作记录'];
  const nextStep: Record<string, string> = {
    PENDING_PAYMENT: '等待客户付款。付款前不进入履约，也不发起退款。',
    PAID_NEED_TRIP_INFO: '已收款，请联系客户补齐出行资料，再提交运营确认。',
    PAID_INFO_READY: '资料已补齐，请核验行程并提交运营确认。',
    PENDING_CONFIRM: '请核对日期、人数及行程资料，确认后安排履约。',
    CONFIRMED: '订单已确认，请开始履约准备并回填服务资源。',
    PENDING_FULFILLMENT: '请填写车辆、向导等服务资源并完成履约检查。',
    FULFILLMENT_READY: '履约准备完成，可在实际出发时开始服务。',
    IN_SERVICE: '行程进行中，服务结束后确认完成。',
    COMPLETED: '服务已完成，相关记录可在此查阅。',
    PAYMENT_CLOSED: '支付已关闭，无须安排履约。符合条件的订单可在列表中删除。',
    CANCELLED: '订单已取消，无须安排履约。',
    REFUND_APPLIED: '客户已申请退款，请进入退款队列核验。',
    REFUND_PROCESSING: '退款处理中，请等待或核验支付平台结果，勿重复退款。',
    REFUNDED: '退款已完成，账务记录保留供核对。',
  };
  const canFulfill = [
    'CONFIRMED',
    'PENDING_FULFILLMENT',
    'FULFILLMENT_READY',
    'IN_SERVICE',
  ].includes(orderStatus);
  return (
    <div className="r3-page order-workbench order-list-first order-detail-page">
      <Header
        title="订单详情"
        subtitle="出行资料、账目与处理记录分区查看"
        actions={
          <a className="r3-button-link secondary" href={pathFor('ADM-E01')}>
            返回订单列表
          </a>
        }
      />
      <State state={orderState} count={Object.keys(order).length} noun="订单">
        <section className="order-summary">
          <div className="order-summary-top">
            <div>
              <small>
                {clean(
                  order.sourceAppName ?? order.appName ?? unwrapRecord(order.app).displayName,
                  '来源小程序未标注',
                )}{' '}
                · {clean(order.cityName)} · {label(order.orderMode)} ·{' '}
                {clean(order.channelName, '自然访问或未标记')} · 处理人{' '}
                {clean(order.assigneeName, '未分配')}
              </small>
              <h2>{orderNo(order)}</h2>
              <small>
                {clean(order.productName, '线路订单')} · {clean(order.skuName, '套餐信息未提供')} ·
                创建于 {dateTime(order.createdAt)}
              </small>
            </div>
            <div className="order-commands">
              <Pill value={order.status} />
              <CopyOrderButton api={api} appId={appId} orderId={orderId} />
              {order.adminDeletedAt ? (
                <span className="canonical-pill orange">已从工作列表删除</span>
              ) : null}
              <button
                type="button"
                className="r3-button-link secondary"
                onClick={orderState.reload}
              >
                刷新订单
              </button>
            </div>
          </div>
          <div className="order-facts">
            <div>
              <span>应付金额</span>
              <strong>{protectedMoney(order.payAmountCents)}</strong>
            </div>
            <div>
              <span>实际支付</span>
              <strong>{protectedMoney(order.actualPaidAmountCents)}</strong>
            </div>
            <div>
              <span>出行日期</span>
              <strong>{dateOnly(trip.travelDate)}</strong>
            </div>
            <div>
              <span>出行人数</span>
              <strong>{clean(trip.peopleCount ?? order.peopleCount, '待补充')}</strong>
            </div>
          </div>
          <div className="order-next">
            <strong>下一步：</strong>
            {nextStep[orderStatus] ?? '请核对当前状态并按退款 / 售后流程处理。'}
          </div>
          <div className="order-commands">
            {canFulfill && (
              <a className="r3-button-link" href={pathFor('ADM-E06', { id: orderId })}>
                处理人工履约
              </a>
            )}
            {['PAID_INFO_READY', 'PENDING_CONFIRM'].includes(orderStatus) && (
              <a className="r3-button-link" href={pathFor('ADM-E05')}>
                进入运营确认队列
              </a>
            )}
            <button
              type="button"
              className="r3-button-link secondary"
              onClick={() => setDetailTab('沟通记录')}
            >
              记录客户沟通
            </button>
            {['REFUND_APPLIED', 'REFUND_PROCESSING', 'REFUNDED'].includes(orderStatus) && (
              <a className="r3-button-link secondary" href={pathFor('ADM-E08')}>
                处理退款申请
              </a>
            )}
          </div>
        </section>
        <nav className="order-tabs" role="tablist" aria-label="订单信息分区">
          {tabs.map((tab, index) => (
            <button
              type="button"
              role="tab"
              id={`order-tab-${index}`}
              aria-selected={detailTab === tab}
              aria-controls="order-detail-panel"
              tabIndex={detailTab === tab ? 0 : -1}
              key={tab}
              onClick={() => setDetailTab(tab)}
              onKeyDown={(event) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const next =
                  event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? tabs.length - 1
                      : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
                setDetailTab(tabs[next]!);
                document.getElementById(`order-tab-${next}`)?.focus();
              }}
            >
              {tab}
            </button>
          ))}
        </nav>
        <section
          className="order-tab-content"
          role="tabpanel"
          id="order-detail-panel"
          aria-labelledby={`order-tab-${tabs.indexOf(detailTab)}`}
        >
          {detailTab === '出行资料' && (
            <Panel title="产品与行程信息">
              <div className="r3-field-list">
                <Field
                  name="产品 / 套餐"
                  value={`${clean(order.productName)} / ${clean(order.skuName)}`}
                />
                <Field name="出行日期" value={dateOnly(trip.travelDate)} />
                <Field
                  name="出行人数"
                  value={clean(trip.peopleCount ?? order.peopleCount, '待补充')}
                />
                <Field name="联系人" value={clean(trip.contactName, '待补充 / 无字段权限')} />
                <Field name="联系电话" value={clean(trip.mobile, '待补充 / 无字段权限')} />
                {trip.pickupAddress ? (
                  <Field name="接送点" value={clean(trip.pickupAddress)} />
                ) : null}
                <BookingOrderInfo
                  details={nested(trip, 'bookingDetails')}
                  summary={nested(order, 'bookingSummary')}
                />
              </div>
            </Panel>
          )}
          {detailTab === '支付与退款' && (
            <Panel title="订单金额与支付事实" hint="优惠仅扣减一次；实际到账以服务端支付结果为准">
              <div className="r3-field-list">
                <Field name="商品原价" value={protectedMoney(order.grossAmountCents)} />
                <Field
                  name="优惠减免"
                  value={
                    typeof order.discountAmountCents === 'number'
                      ? `-${money(order.discountAmountCents)}`
                      : '无权限查看'
                  }
                />
                <Field name="应付金额" value={protectedMoney(order.payAmountCents)} />
                <Field name="实际支付" value={protectedMoney(order.actualPaidAmountCents)} />
                <Field name="支付状态" value={paymentLabel(order.paymentStatus)} />
                <Field name="支付时间" value={dateTime(order.paidAt)} />
                <Field name="退款金额" value={protectedMoney(order.refundAmountCents)} />
                <Field
                  name="退款状态"
                  value={label(
                    order.refundStatus,
                    ['REFUND_APPLIED', 'REFUND_PROCESSING', 'REFUNDED'].includes(orderStatus)
                      ? label(orderStatus)
                      : '无退款记录',
                  )}
                />
              </div>
              {['REFUND_APPLIED', 'REFUND_PROCESSING', 'REFUNDED'].includes(orderStatus) && (
                <a className="r3-button-link secondary" href={pathFor('ADM-E08')}>
                  查看退款申请
                </a>
              )}
            </Panel>
          )}
          {detailTab === '履约处理' && (
            <Panel title="履约准备与服务进度">
              <p className="order-next">{nextStep[orderStatus] ?? '请按当前订单状态处理。'}</p>
              <div className="r3-field-list">
                <Field name="车辆安排" value={clean(fulfillment.vehicleDetail, '尚未安排')} />
                <Field name="向导安排" value={clean(fulfillment.guideDetail, '尚未安排')} />
                <Field name="票务安排" value={clean(fulfillment.ticketDetail, '尚未安排')} />
                <Field name="接送时间" value={clean(fulfillment.pickupTime, '尚未安排')} />
                <Field name="履约备注" value={clean(fulfillment.serviceRemark, '暂无备注')} />
              </div>
              {checklist.length > 0 ? (
                <div className="r3-checklist compact">
                  {checklist.map((item) => (
                    <div key={clean(item.code)}>
                      <span>{item.completed ? '✓' : '○'}</span>
                      <strong>{checklistLabels[clean(item.code)] ?? '履约检查项'}</strong>
                      <small>{item.completed ? '已完成' : '待处理'}</small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="order-hint">尚未生成履约检查项。</p>
              )}
              {canFulfill && (
                <a className="r3-button-link" href={pathFor('ADM-E06', { id: orderId })}>
                  进入履约操作
                </a>
              )}
            </Panel>
          )}
          {detailTab === '沟通记录' && (
            <div className="r3-detail-grid">
              <Panel title="记录客户沟通">
                <CommunicationForm
                  api={api}
                  appId={appId}
                  orderId={orderId}
                  onDone={communications.reload}
                />
              </Panel>
              <Panel title="沟通历史">
                <State state={communications} count={communicationRows.length} noun="沟通记录">
                  <div className="r3-timeline compact">
                    {communicationRows.map((item) => (
                      <article key={textOf(item, 'id')}>
                        <span />
                        <div>
                          <small>
                            {dateTime(item.createdAt)} · {label(item.channel)} ·{' '}
                            {label(item.direction)}
                          </small>
                          <strong>{clean(item.summary)}</strong>
                        </div>
                      </article>
                    ))}
                  </div>
                </State>
              </Panel>
            </div>
          )}
          {detailTab === '操作记录' && (
            <Panel title="订单状态流转" hint="最近记录在前；删除 / 恢复记录在后台操作日志中保留">
              <div className="r3-timeline compact">
                {logs.map((log) => (
                  <article key={textOf(log, 'id')}>
                    <span />
                    <div>
                      <small>
                        {dateTime(log.createdAt)} · {clean(log.actorType, '系统')}
                      </small>
                      <strong>
                        {log.fromStatus ? `${label(log.fromStatus)} → ` : ''}
                        {label(log.toStatus)}
                      </strong>
                    </div>
                  </article>
                ))}
                {!logs.length && <p className="order-hint">暂无状态流转记录。</p>}
              </div>
            </Panel>
          )}
        </section>
      </State>
    </div>
  );
}

function RefundPage({
  api,
  appId,
  state,
  items,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  items: JsonRecord[];
}) {
  const accessState = useApi(api, '/api/admin/m8/security/me');
  const permissionCodes = unwrapRecord(accessState.data).permissionCodes;
  const canProcess = Array.isArray(permissionCodes) && permissionCodes.includes('refund.process');
  const [selectedId, setSelectedId] = useState('');
  const selected = items.find((item) => textOf(item, 'id') === selectedId) ?? items[0];
  const status = selected ? textOf(selected, 'status') : '';
  const policy = selected ? nested(nested(selected, 'order'), 'refundPolicySnapshot') : {};
  const hasAmountAccess = Boolean(selected && typeof selected.amountCents === 'number');
  const allAmountsVisible = items.every((item) => typeof item.amountCents === 'number');
  const selectedRecordId = textOf(selected ?? {}, 'id');
  const [reviewReasons, setReviewReasons] = useState<Record<string, string>>({});
  const reviewReason = reviewReasons[selectedRecordId] ?? '';
  const setReviewReason = (value: string) =>
    setReviewReasons((current) => ({ ...current, [selectedRecordId]: value }));
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [executeConfirmed, setExecuteConfirmed] = useState(false);
  useEffect(() => {
    setReviewConfirmed(false);
    setExecuteConfirmed(false);
  }, [selectedRecordId]);
  return (
    <div className="r3-page">
      <Header title="退款申请处理" subtitle="核验原支付事实、退款规则与原商户路由" />
      <Metrics
        items={[
          {
            label: '退款申请',
            value: items.filter((item) => textOf(item, 'status') === 'APPLIED').length,
          },
          {
            label: '处理中',
            value: items.filter((item) =>
              ['APPROVED', 'PROCESSING'].includes(textOf(item, 'status')),
            ).length,
          },
          {
            label: '退款金额',
            value: allAmountsVisible
              ? money(items.reduce((sum, item) => sum + Number(item.amountCents), 0))
              : '无权限查看',
          },
          {
            label: '异常退款',
            value: items.filter((item) => textOf(item, 'status') === 'FAILED').length,
          },
        ]}
      />
      <div className="r3-side-layout">
        <Panel title="退款申请列表">
          <State state={state} count={items.length} noun="退款申请">
            <Table
              heads={['订单', '申请原因', '申请金额', '原商户校验', '状态', '操作']}
              empty={!items.length}
            >
              {items.map((item) => (
                <tr
                  key={textOf(item, 'id')}
                  className={textOf(item, 'id') === selectedRecordId ? 'r3-selected-record' : ''}
                >
                  <td>{orderNo(nested(item, 'order'))}</td>
                  <td>{clean(item.reason)}</td>
                  <td>{protectedMoney(item.amountCents)}</td>
                  <td>{item.originalPaymentMchid ? '已绑定原支付事实' : '等待支付事实'}</td>
                  <td>
                    <Pill value={item.status} />
                  </td>
                  <td>
                    <button type="button" onClick={() => setSelectedId(textOf(item, 'id'))}>
                      {canProcess ? '处理' : '查看'}
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          </State>
        </Panel>
        <Panel
          key={selectedRecordId}
          title={canProcess ? '退款审核' : '退款详情（只读）'}
          hint={selected ? `关联订单 ${orderNo(nested(selected, 'order'))}` : '请选择退款申请'}
        >
          {selected ? (
            <>
              <div className="r3-field-list">
                <Field name="申请金额" value={protectedMoney(selected.amountCents)} />
                <Field name="申请原因" value={clean(selected.reason)} />
                <Field name="当前状态" value={<Pill value={selected.status} />} />
                <Field
                  name="原路退款"
                  value={selected.originalPaymentMchid ? '已锁定原支付商户' : '等待原支付事实'}
                />
                <Field name="退款规则版本" value={clean(policy.version, '尚未取得')} />
                <Field name="退款说明" value={clean(policy.notice, '尚未取得')} />
              </div>
              {!canProcess && <Notice>当前账号可查看退款记录。审核及执行退款需单独授权。</Notice>}
              {canProcess &&
                status === 'APPLIED' &&
                (Object.keys(policy).length && hasAmountAccess ? (
                  <div className="r3-command-stack">
                    <label className="r3-note-field">
                      审核说明
                      <textarea
                        value={reviewReason}
                        onChange={(event) => setReviewReason(event.target.value)}
                        placeholder="填写同意或驳回依据"
                      />
                    </label>
                    <label className="r3-inline-check">
                      <input
                        type="checkbox"
                        checked={reviewConfirmed}
                        onChange={(event) => setReviewConfirmed(event.target.checked)}
                      />
                      已核对订单时点退款规则、金额与原支付事实
                    </label>
                    <div className="r3-row-actions">
                      <MutationButton
                        api={api}
                        path={`/api/admin/m4/refunds/${encodeURIComponent(textOf(selected, 'id'))}/review?appId=${encodeURIComponent(appId)}`}
                        body={{
                          expectedVersion: Number(selected.version ?? 0),
                          decision: 'APPROVE',
                          reason: reviewReason,
                        }}
                        idempotent
                        disabled={!reviewConfirmed || !reviewReason.trim()}
                        label="同意退款"
                        onDone={state.reload}
                      />
                      <MutationButton
                        api={api}
                        path={`/api/admin/m4/refunds/${encodeURIComponent(textOf(selected, 'id'))}/review?appId=${encodeURIComponent(appId)}`}
                        body={{
                          expectedVersion: Number(selected.version ?? 0),
                          decision: 'REJECT',
                          reason: reviewReason,
                        }}
                        idempotent
                        danger
                        disabled={!reviewConfirmed || !reviewReason.trim()}
                        label="驳回申请"
                        onDone={state.reload}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="canonical-unavailable compact">
                    <span>!</span>
                    <div>
                      <strong>退款审核暂不可提交</strong>
                      <p>
                        {!Object.keys(policy).length
                          ? '未取得订单时点退款规则，为避免缺少依据的资金操作，本页保持禁用。'
                          : '当前账号无退款金额字段权限，不能审核资金操作。'}
                      </p>
                    </div>
                  </div>
                ))}
              {canProcess &&
                ['APPROVED', 'PROCESSING', 'FAILED'].includes(status) &&
                (hasAmountAccess && selected.originalPaymentMchid ? (
                  <div className="r3-command-stack">
                    <label className="r3-inline-check">
                      <input
                        type="checkbox"
                        checked={executeConfirmed}
                        onChange={(event) => setExecuteConfirmed(event.target.checked)}
                      />
                      已再次核对退款金额与原支付商户
                    </label>
                    <MutationButton
                      api={api}
                      path={`/api/admin/m4/refunds/${encodeURIComponent(textOf(selected, 'id'))}/execute?appId=${encodeURIComponent(appId)}`}
                      disabled={!executeConfirmed}
                      label="执行原路退款"
                      onDone={state.reload}
                    />
                  </div>
                ) : (
                  <div className="canonical-unavailable compact">
                    <span>!</span>
                    <div>
                      <strong>执行退款暂不可提交</strong>
                      <p>需要退款金额字段权限及原支付商户事实，服务端会再次强制校验。</p>
                    </div>
                  </div>
                ))}
            </>
          ) : (
            <Empty noun="可处理退款" />
          )}
        </Panel>
      </div>
    </div>
  );
}

function AfterSalesPage({
  api,
  appId,
  state,
  items,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  items: JsonRecord[];
}) {
  const [selectedId, setSelectedId] = useState('');
  const [query, setQuery] = useState('');
  const visibleItems = items.filter((item) => {
    const needle = query.trim().toLowerCase();
    return (
      !needle ||
      orderNo(nested(item, 'order')).toLowerCase().includes(needle) ||
      clean(item.subject, '').toLowerCase().includes(needle)
    );
  });
  const selected =
    visibleItems.find((item) => textOf(item, 'id') === selectedId) ?? visibleItems[0];
  const selectedTicketId = textOf(selected ?? {}, 'id');
  const [ticketNotes, setTicketNotes] = useState<Record<string, string>>({});
  const note = ticketNotes[selectedTicketId] ?? '';
  const setNote = (value: string) =>
    setTicketNotes((current) => ({ ...current, [selectedTicketId]: value }));
  const ticketStatus = selected ? textOf(selected, 'status') : '';
  const nextStatus =
    ticketStatus === 'OPEN'
      ? 'PROCESSING'
      : ticketStatus === 'PROCESSING'
        ? 'RESOLVED'
        : ticketStatus === 'RESOLVED'
          ? 'CLOSED'
          : '';
  const nextStatusLabel =
    nextStatus === 'PROCESSING'
      ? '开始处理'
      : nextStatus === 'RESOLVED'
        ? '标记已解决'
        : '关闭工单';
  return (
    <div className="r3-page">
      <Header
        title="售后工单"
        subtitle="问题受理、跨部门协同、处理记录与关闭"
        actions={<span className="r3-source-note">工单由订单详情或用户售后申请创建</span>}
      />
      <Filters
        query={query}
        onQuery={setQuery}
        statusText="全部工单状态"
        placeholder="输入订单号或问题摘要"
      />
      <div className="r3-side-layout">
        <Panel title="售后工单">
          <State state={state} count={visibleItems.length} noun="售后工单">
            <Table
              heads={['工单', '关联订单', '问题类型', '优先级', '状态', '操作']}
              empty={!items.length}
            >
              {visibleItems.map((item) => (
                <tr
                  key={textOf(item, 'id')}
                  className={textOf(item, 'id') === selectedTicketId ? 'r3-selected-record' : ''}
                >
                  <td>
                    <strong>{clean(item.ticketNo, '售后工单')}</strong>
                  </td>
                  <td>{orderNo(nested(item, 'order'))}</td>
                  <td>{clean(item.issueType ?? item.reason, '服务问题')}</td>
                  <td>{label(item.priority, '普通')}</td>
                  <td>
                    <Pill value={item.status} />
                  </td>
                  <td>
                    <button type="button" onClick={() => setSelectedId(textOf(item, 'id'))}>
                      进入工单
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          </State>
        </Panel>
        <Panel
          key={selectedTicketId}
          title={selected ? clean(selected.ticketNo, '工单处理') : '工单处理'}
        >
          {selected ? (
            <>
              <div className="r3-field-list">
                <Field
                  name="问题摘要"
                  value={clean(selected.subject ?? selected.summary ?? selected.reason)}
                />
                <Field name="关联订单" value={orderNo(nested(selected, 'order'))} />
                <Field name="当前状态" value={<Pill value={selected.status} />} />
                <Field name="处理人" value={selected.assignedTo ? '已分配' : '待分配'} />
              </div>
              <label className="r3-note-field">
                新增处理记录
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="记录本次沟通与处理结果"
                />
              </label>
              <div className="r3-command-stack">
                <MutationButton
                  api={api}
                  path={`/api/admin/m4/after-sales/${encodeURIComponent(textOf(selected, 'id'))}/communications?appId=${encodeURIComponent(appId)}`}
                  body={{ summary: note }}
                  disabled={!note.trim()}
                  label="新增处理记录"
                  onDone={() => {
                    setNote('');
                    state.reload();
                  }}
                />
                {nextStatus ? (
                  <MutationButton
                    api={api}
                    path={`/api/admin/m4/after-sales/${encodeURIComponent(textOf(selected, 'id'))}/transitions?appId=${encodeURIComponent(appId)}`}
                    body={{ expectedVersion: Number(selected.version ?? 0), status: nextStatus }}
                    idempotent
                    label={nextStatusLabel}
                    onDone={state.reload}
                  />
                ) : (
                  <span className="r3-closed-state">工单已关闭</span>
                )}
              </div>
            </>
          ) : (
            <Empty noun="可处理工单" />
          )}
        </Panel>
      </div>
    </div>
  );
}

function ExportPage({
  api,
  appId,
  state,
  items,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  items: JsonRecord[];
}) {
  const [result, setResult] = useState('');
  const [creating, setCreating] = useState(false);
  const [includeContact, setIncludeContact] = useState(false);
  const [includeFinance, setIncludeFinance] = useState(false);
  async function download(item: JsonRecord) {
    setResult('');
    try {
      const jobId = textOf(item, 'id');
      const grant = unwrapRecord(
        await api.send(
          `/api/admin/m7/apps/${encodeURIComponent(appId)}/exports/${encodeURIComponent(jobId)}/download-grant`,
          'POST',
        ),
      );
      const blob = await api.download(
        `/api/admin/m7/apps/${encodeURIComponent(appId)}/exports/${encodeURIComponent(jobId)}/download?token=${encodeURIComponent(textOf(grant, 'token'))}`,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = clean(item.fileName, '订单导出文件.xlsx');
      link.click();
      URL.revokeObjectURL(url);
      setResult('文件下载已开始');
    } catch (error) {
      setResult(operationError(error));
    }
  }
  async function create() {
    if (creating) return;
    setCreating(true);
    setResult('');
    try {
      const fields = [
        'orderNo',
        'status',
        'cityId',
        'productId',
        'skuId',
        'salesAmountCents',
        'discountAmountCents',
        ...(includeContact ? ['contactName', 'mobile'] : []),
        ...(includeFinance ? ['paidAmountCents', 'refundAmountCents'] : []),
      ];
      let confirmationToken: string | undefined;
      if (includeContact || includeFinance) {
        const confirmation = unwrapRecord(
          await api.send(
            `/api/admin/m7/apps/${encodeURIComponent(appId)}/export-confirmations`,
            'POST',
            { dataset: 'ORDER_DETAIL', fields },
          ),
        );
        confirmationToken = textOf(confirmation, 'token');
      }
      await api.send(`/api/admin/m7/apps/${encodeURIComponent(appId)}/exports`, 'POST', {
        dataset: 'ORDER_DETAIL',
        format: 'XLSX',
        fields,
        filters: {},
        idempotencyKey: mutationKey('order-export'),
        ...(confirmationToken && confirmationToken !== '—' ? { confirmationToken } : {}),
      });
      setResult('导出任务已提交；字段会由服务端按 L0/L1/L2 权限再次裁剪。');
      state.reload();
    } catch (error) {
      setResult(operationError(error));
    } finally {
      setCreating(false);
    }
  }
  return (
    <div className="r3-page">
      <Header title="订单导出任务" subtitle="按当前账号数据范围异步生成，敏感字段需要独立权限" />
      <Metrics
        items={[
          { label: '导出任务', value: items.length },
          {
            label: '处理中',
            value: items.filter((item) =>
              ['PENDING', 'PROCESSING'].includes(textOf(item, 'status')),
            ).length,
          },
          {
            label: '可下载',
            value: items.filter((item) => textOf(item, 'status') === 'READY').length,
          },
          {
            label: '已过期',
            value: items.filter((item) => textOf(item, 'status') === 'EXPIRED').length,
          },
        ]}
      />
      <div className="r3-side-layout">
        <Panel title="订单导出任务">
          <State state={state} count={items.length} noun="导出任务">
            <Table
              heads={['申请时间', '数据范围', '字段级别', '文件', '状态', '操作']}
              empty={!items.length}
            >
              {items.map((item) => (
                <tr key={textOf(item, 'id')}>
                  <td>{dateTime(item.createdAt)}</td>
                  <td>按当前账号管理范围裁剪</td>
                  <td>
                    {(() => {
                      const fields = stringList(item.approvedFields).length
                        ? stringList(item.approvedFields)
                        : stringList(item.requestedFields);
                      if (
                        fields.some((field) =>
                          [
                            'costAmountCents',
                            'commissionAmountCents',
                            'grossProfitAmountCents',
                            'refundAmountCents',
                          ].includes(field),
                        )
                      )
                        return '含财务字段';
                      if (
                        fields.some((field) =>
                          ['contactName', 'mobile', 'wechatNo'].includes(field),
                        )
                      )
                        return '含联系方式';
                      return '普通运营字段';
                    })()}
                  </td>
                  <td>{clean(item.fileName, '生成中')}</td>
                  <td>
                    <Pill value={item.status} />
                  </td>
                  <td>
                    {textOf(item, 'status') === 'READY' ? (
                      <button type="button" onClick={() => void download(item)}>
                        安全下载
                      </button>
                    ) : (
                      <span className="r3-muted">等待生成</span>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </State>
        </Panel>
        <Panel title="新建导出任务" hint="默认仅导出普通运营字段">
          <div className="r3-checklist editable">
            <p className="r3-export-base-fields">
              已包含：订单号、状态、城市、产品、套餐及销售金额
            </p>
            <label>
              <input
                type="checkbox"
                checked={includeContact}
                onChange={(event) => setIncludeContact(event.target.checked)}
              />
              <strong>用户联系方式（需 L1 权限）</strong>
            </label>
            <label>
              <input
                type="checkbox"
                checked={includeFinance}
                onChange={(event) => setIncludeFinance(event.target.checked)}
              />
              <strong>实付与退款金额（需 L2 权限）</strong>
            </label>
          </div>
          <button
            type="button"
            className="canonical-primary"
            disabled={creating || state.loading || Boolean(state.error)}
            onClick={() => void create()}
          >
            {creating ? '正在提交…' : '提交异步导出任务'}
          </button>
          {result && (
            <p className="r3-result" role="status">
              {result}
            </p>
          )}
        </Panel>
      </div>
    </div>
  );
}

export function CanonicalOrderPage({
  api,
  targetAppId,
  pageId,
  params,
  scopeFilter,
  scopePagination,
  onFiltersChange,
}: CanonicalPageProps) {
  const aggregate = targetAppId === AUTHORIZED_SCOPE;
  const [deleted, setDeleted] = useState(
    () => new URLSearchParams(location.search).get('deleted') === 'true',
  );
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [query, setQuery] = useState(
    () => new URLSearchParams(location.search).get('keyword') ?? '',
  );
  const [searchInput, setSearchInput] = useState(
    () => new URLSearchParams(location.search).get('keyword') ?? '',
  );
  const orderStatuses = [
    'PENDING_PAYMENT',
    'PAYMENT_CLOSED',
    'PAID_NEED_TRIP_INFO',
    'PAID_INFO_READY',
    'PENDING_CONFIRM',
    'CONFIRMED',
    'PENDING_FULFILLMENT',
    'COMPLETED',
    'CANCELLED',
    'REFUND_APPLIED',
    'REFUND_PROCESSING',
    'REFUNDED',
    'AFTER_SALES',
  ];
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const value = new URLSearchParams(location.search).get('status') ?? '';
    return orderStatuses.includes(value) ? value : '';
  });
  const status =
    pageId === 'ADM-E03'
      ? 'PENDING_PAYMENT'
      : pageId === 'ADM-E04'
        ? 'PAID_NEED_TRIP_INFO'
        : pageId === 'ADM-E05'
          ? 'PENDING_CONFIRM'
          : selectedStatus;
  const listPath = targetAppId
    ? `/api/admin/m4/orders?appId=${encodeURIComponent(targetAppId)}${status ? `&status=${status}` : ''}${deleted ? '&deleted=true' : ''}${query ? `&keyword=${encodeURIComponent(query)}` : ''}`
    : undefined;
  const listState = useApi(
    api,
    !['ADM-E02', 'ADM-E06', 'ADM-E07', 'ADM-E08', 'ADM-E09', 'ADM-E10'].includes(pageId)
      ? listPath
      : undefined,
  );
  const list = rows(listState.data);
  const detailState = useApi(
    api,
    ['ADM-E02', 'ADM-E06', 'ADM-E07'].includes(pageId) && targetAppId && params.id
      ? `/api/admin/m4/orders/${encodeURIComponent(params.id)}?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const order = unwrapRecord(detailState.data);
  const refundsState = useApi(
    api,
    pageId === 'ADM-E08' && targetAppId
      ? `/api/admin/m4/refunds?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const afterSalesState = useApi(
    api,
    pageId === 'ADM-E09' && targetAppId
      ? `/api/admin/m4/after-sales?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const exportsState = useApi(
    api,
    pageId === 'ADM-E10' && targetAppId
      ? `/api/admin/m7/apps/${encodeURIComponent(targetAppId)}/exports`
      : undefined,
  );
  const [localPage, setLocalPage] = useState(1);
  useEffect(() => {
    if (!['ADM-E01', 'ADM-E03', 'ADM-E04', 'ADM-E05'].includes(pageId)) return;
    const url = new URL(location.href);
    if (pageId === 'ADM-E01' && selectedStatus) url.searchParams.set('status', selectedStatus);
    else url.searchParams.delete('status');
    if (query) url.searchParams.set('keyword', query);
    else url.searchParams.delete('keyword');
    if (deleted) url.searchParams.set('deleted', 'true');
    else url.searchParams.delete('deleted');
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [pageId, query, selectedStatus, deleted]);
  useEffect(() => {
    setSelectedOrders([]);
    setLocalPage(1);
  }, [targetAppId, status, query, deleted, pageId]);
  const serverPaged = isRecord(listState.data) && typeof listState.data.page === 'number';
  const visible = useMemo(
    () =>
      list.filter(
        (item) =>
          serverPaged || !query || orderNo(item).toLowerCase().includes(query.toLowerCase()),
      ),
    [list, query, serverPaged],
  );
  const localPages = Math.max(1, Math.ceil(visible.length / 20));
  const currentLocalPage = Math.min(localPage, localPages);
  const tableOrders = serverPaged
    ? visible
    : visible.slice((currentLocalPage - 1) * 20, currentLocalPage * 20);
  if (!targetAppId)
    return <Empty noun="目标小程序" detail="请先在顶部选择目标小程序，再查看订单。" />;
  if (['ADM-E02', 'ADM-E06', 'ADM-E07'].includes(pageId))
    return (
      <OrderDetail
        api={api}
        appId={targetAppId}
        pageId={pageId}
        orderState={detailState}
        order={order}
      />
    );
  if (pageId === 'ADM-E08')
    return (
      <RefundPage
        api={api}
        appId={targetAppId}
        state={refundsState}
        items={rows(refundsState.data)}
      />
    );
  if (pageId === 'ADM-E09')
    return (
      <AfterSalesPage
        api={api}
        appId={targetAppId}
        state={afterSalesState}
        items={rows(afterSalesState.data)}
      />
    );
  if (pageId === 'ADM-E10')
    return (
      <ExportPage
        api={api}
        appId={targetAppId}
        state={exportsState}
        items={rows(exportsState.data)}
      />
    );
  const title =
    pageId === 'ADM-E03'
      ? '待支付订单'
      : pageId === 'ADM-E04'
        ? '已支付待补资料'
        : pageId === 'ADM-E05'
          ? '待运营确认'
          : '订单管理';
  const subtitle = pageId === 'ADM-E01' ? '统一查看标准预订与快捷预订订单' : `订单管理 / ${title}`;
  return (
    <div className="r3-page order-workbench order-list-first order-list-page">
      <Header
        title={title}
        subtitle={subtitle}
        actions={
          pageId === 'ADM-E01' ? (
            <a className="r3-button-link" href={pathFor('ADM-E10')}>
              导出任务
            </a>
          ) : undefined
        }
      />
      {scopeFilter}
      <form
        className="order-list-search"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(searchInput.trim());
          onFiltersChange?.();
        }}
      >
        <label>
          <span>订单号</span>
          <input
            type="search"
            aria-label="搜索订单号"
            placeholder="输入订单号查询"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
        <button type="submit">查询</button>
        <button
          type="button"
          disabled={!query && !searchInput}
          onClick={() => {
            setQuery('');
            setSearchInput('');
            onFiltersChange?.();
          }}
        >
          清除搜索
        </button>
        <button type="button" onClick={listState.reload}>
          刷新列表
        </button>
        <small>
          {aggregate ? '全部授权数据' : '当前小程序数据'} ·{' '}
          {listState.loading
            ? '正在读取'
            : listState.error
              ? '读取未完成'
              : `本页 ${visible.length} 条`}
        </small>
      </form>
      {pageId === 'ADM-E01' && (
        <nav className="order-status-filters" aria-label="订单状态筛选">
          {[
            { value: '', name: '全部状态' },
            ...orderStatuses.map((value) => ({
              value,
              name: value === 'PAID_INFO_READY' ? '资料已齐' : label(value),
            })),
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={selectedStatus === item.value}
              onClick={() => {
                setSelectedStatus(item.value);
                onFiltersChange?.();
              }}
            >
              {item.name}
            </button>
          ))}
        </nav>
      )}
      <Panel title={deleted ? '已删除订单 · 可恢复' : title}>
        {pageId !== 'ADM-E05' && (
          <OrderBatchActions
            key={`${targetAppId}:${status}:${deleted}:${query}`}
            api={api}
            appId={targetAppId}
            orders={listState.loading || listState.error ? [] : tableOrders}
            selected={selectedOrders}
            onSelect={setSelectedOrders}
            deleted={deleted}
            onDeleted={(value) => {
              setDeleted(value);
              onFiltersChange?.();
            }}
            onDone={listState.reload}
          />
        )}
        <State state={listState} count={visible.length} noun="订单">
          {pageId === 'ADM-E05' ? (
            <ConfirmationQueue
              api={api}
              appId={targetAppId}
              state={listState}
              orders={tableOrders}
            />
          ) : (
            <OrderTable
              api={api}
              appId={targetAppId}
              orders={tableOrders}
              selected={selectedOrders}
              onSelect={setSelectedOrders}
            />
          )}
        </State>
        {scopePagination}
        {!scopePagination &&
          !serverPaged &&
          !listState.loading &&
          !listState.error &&
          localPages > 1 && (
            <nav className="order-local-pagination" aria-label="订单分页">
              <small>共 {visible.length} 条 · 每页 20 条</small>
              <button
                type="button"
                disabled={currentLocalPage <= 1}
                onClick={() => {
                  setSelectedOrders([]);
                  setLocalPage(currentLocalPage - 1);
                }}
              >
                上一页
              </button>
              <span aria-live="polite">
                {currentLocalPage} / {localPages}
              </span>
              <button
                type="button"
                disabled={currentLocalPage >= localPages}
                onClick={() => {
                  setSelectedOrders([]);
                  setLocalPage(currentLocalPage + 1);
                }}
              >
                下一页
              </button>
            </nav>
          )}
      </Panel>
    </div>
  );
}

function BookingOrderInfo({ details, summary }: { details: JsonRecord; summary: JsonRecord }) {
  if (!Object.keys(details).length && !Object.keys(summary).length) return null;
  const travelers = Array.isArray(details.travelers) ? details.travelers.filter(isRecord) : [];
  return (
    <>
      {[
        'meetingPoint',
        'accommodation',
        'lodgingNights',
        'childBed',
        'vehicle',
        'extras',
        'confirmation',
      ]
        .filter((key) => summary[key])
        .map((key) => (
          <Field
            key={key}
            name={
              (
                {
                  meetingPoint: '集合地点',
                  lodgingNights: '住宿晚数',
                  childBed: '儿童占床',
                  confirmation: '班期确认',
                  accommodation: '住宿方案',
                  vehicle: '出行车辆',
                  extras: '附加服务',
                } as Record<string, string>
              )[key]!
            }
            value={clean(summary[key])}
          />
        ))}
      {(typeof details.adults === 'number' || typeof details.children === 'number') && (
        <Field
          name="成人 / 儿童"
          value={`${details.adults ?? '待补充'} / ${details.children ?? '待补充'}`}
        />
      )}
      {travelers.length > 0 && (
        <div className="booking-order-travelers">
          <strong>实际出行人</strong>
          {travelers.map((traveler, index) => (
            <div
              key={index}
              style={{ display: 'flex', gap: 12, padding: '6px 0', flexWrap: 'wrap' }}
            >
              <span>
                {index + 1}. {clean(traveler.name, '姓名未采集')}
              </span>
              {Boolean(traveler.documentNumber) && (
                <span>
                  {traveler.documentType === 'PASSPORT' ? '护照' : '身份证'}：
                  {clean(traveler.documentNumber)}
                </span>
              )}
              {Boolean(traveler.mobile) && <span>{clean(traveler.mobile)}</span>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
